import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  FileText,
  RefreshCw,
  Calendar,
  Users,
  Briefcase,
  Layers,
  Search,
  Plus,
  Trash2,
  CheckCircle,
  FileCheck,
  Send,
  PlusCircle,
  TrendingUp,
  DollarSign,
  AlertCircle,
  MapPin,
  Mail,
  Phone,
  Printer,
  ChevronDown,
  Signature,
  Zap,
  Info,
  ExternalLink,
  Loader2,
  Package,
  X,
  Columns,
  Filter,
  ArrowUpDown,
  Bookmark,
  Sparkles,
  Clock,
  Edit3,
  Save,
  Check,
  RotateCcw,
  Sliders,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { 
  fetchShippers, saveShipper, deleteShipper,
  fetchConsignees, saveConsignee, deleteConsignee,
  fetchBillTos, saveBillTo, deleteBillTo,
  fetchCommodities, saveCommodity, deleteCommodity,
  fetchLaraEmails, saveLaraEmail, deleteLaraEmail,
  fetchNonltlShipments, saveNonltlShipment, deleteNonltlShipment,
  fetchAuditLog, AuditEntry
} from '../lib/db';
import { Shipper, Consignee, BillTo, Commodity, LaraEmail, NonltlShipment, FilterRule, SortRule, SmartView, UserRole, CRMUser } from '../types';
import { fetchSheetRows, isGoogleTokenExpired, notifyGoogleTokenExpired } from '../lib/googleApi';
import { sheetSyncNonltl } from '../lib/db';
import ColumnCustomizerModal, { ColumnSetting } from './ColumnCustomizerModal';
import AdvancedFilterModal from './AdvancedFilterModal';
import MultiSortModal from './MultiSortModal';
import SmartViewSelector from './SmartViewSelector';

// Default column widths for Produce & Non-Floral CRM grid
const DEFAULT_COLUMN_WIDTHS: Record<string, number> = {
  outboundDate: 110,
  shipperName: 160,
  consigneeName: 170,
  billToName: 150,
  poNumber: 110,
  pickupNumber: 110,
  apptReq: 240,
  produceNonltl: 80,
  quoteNumber: 110,
  palletCount: 130,
  pickUpDate: 110,
  arriveAtCustomerDate: 140,
  pickUpTime: 100,
  palletTenderedBy: 140,
  ratePerPallet: 120,
  salesperson: 130,
  notes: 180,
  status: 120,
  actions: 90,
};

// Default grid columns configuration for Produce & Non-Floral CRM
export const DEFAULT_NONFLORAL_COLUMNS: ColumnSetting[] = [
  { id: 'outboundDate', label: 'Outbound Date', visible: true },
  { id: 'shipperName', label: 'Shipper', visible: true },
  { id: 'consigneeName', label: 'Consignee', visible: true },
  { id: 'billToName', label: 'Bill To', visible: true },
  { id: 'poNumber', label: 'PO Number', visible: true },
  { id: 'pickupNumber', label: 'Pick Up #', visible: true },
  { id: 'apptReq', label: 'FCFS / APPT', visible: true },
  { id: 'produceNonltl', label: 'Type', visible: true },
  { id: 'quoteNumber', label: 'Quote #', visible: true },
  { id: 'palletCount', label: 'Pallets / Commodity', visible: true },
  { id: 'pickUpDate', label: 'Pick-Up Date', visible: true },
  { id: 'arriveAtCustomerDate', label: 'Arrive at Customer Date', visible: true },
  { id: 'pickUpTime', label: 'Pick-Up Time', visible: true },
  { id: 'palletTenderedBy', label: 'Pallet Tendered By', visible: true },
  { id: 'ratePerPallet', label: 'Rate ($) / Total ($)', visible: true },
  { id: 'salesperson', label: 'Salesperson / Owner', visible: true },
  { id: 'notes', label: 'Notes', visible: true },
  { id: 'status', label: 'Status', visible: true },
  { id: 'actions', label: 'Actions', visible: true },
];

export const NONFLORAL_FIELD_OPTIONS = [
  { id: 'outboundDate', label: 'Outbound Date' },
  { id: 'shipperName', label: 'Shipper' },
  { id: 'consigneeName', label: 'Consignee' },
  { id: 'billToName', label: 'Bill To' },
  { id: 'poNumber', label: 'PO Number' },
  { id: 'pickupNumber', label: 'Pick Up #' },
  { id: 'apptReq', label: 'FCFS / APPT' },
  { id: 'quoteNumber', label: 'Quote #' },
  { id: 'palletCount', label: 'Pallet Count' },
  { id: 'commodity', label: 'Commodity' },
  { id: 'pickUpDate', label: 'Pick-Up Date' },
  { id: 'arriveAtCustomerDate', label: 'Arrive at Customer Date' },
  { id: 'pickUpTime', label: 'Pick-Up Time' },
  { id: 'palletTenderedBy', label: 'Pallet Tendered By / Carrier' },
  { id: 'ratePerPallet', label: 'Rate Per Pallet ($)' },
  { id: 'salesperson', label: 'Salesperson' },
  { id: 'notes', label: 'Notes' },
  { id: 'status', label: 'Status' },
];

// One distinct badge color per "Pallet Tendered By" carrier value
const CARRIER_COLORS: Record<string, string> = {
  "Lara's Trucking": '#a25ddc',
  'Shipper Delivery': '#0086f0',
  'AEL PLM Pickup': '#00c875',
  'AEL MIA Pickup': '#fdab3d',
  'AEL OXN Pickup': '#f43f5e',
  'AEL PIE Pickup': '#6366f1',
};

type NonFloralTab = 'active' | 'history' | 'lara' | 'maintenance' | 'audit';

interface NonFloralProps {
  currentUserEmail: string;
  userRole?: UserRole;
  subTab: NonFloralTab;
  onSubTabChange: (tab: NonFloralTab) => void;
  accessToken: string | null;
  users?: CRMUser[];
}

const NONLTL_SHEET_ID = '1_fii8vRGfndkejoa6dhBbTHqHp3pRq_OPyeRwS0ukvo';

export default function NonFloral({ currentUserEmail, userRole, subTab, onSubTabChange, accessToken, users }: NonFloralProps) {
  // Check if active user role can view financial numbers
  const canViewFinancials = userRole !== 'warehouse';

  // Module active subtab is owned by App so the sidebar submenu can drive it
  const activeSubTab = subTab;
  const setActiveTabState = onSubTabChange;
  // Module active subtab
  const [maintenanceSubTab, setMaintenanceSubTab] = useState<'shippers' | 'consignees' | 'billtos' | 'commodities'>('shippers');

  // Loading states
  const [loading, setLoading] = useState(true);

  // Database states
  const [shipments, setShipments] = useState<NonltlShipment[]>([]);
  const [shippersList, setShippersList] = useState<Shipper[]>([]);
  const [consigneesList, setConsigneesList] = useState<Consignee[]>([]);
  const [billTosList, setBillTosList] = useState<BillTo[]>([]);
  const [commoditiesList, setCommodityList] = useState<Commodity[]>([]);
  const [laraEmailsList, setLaraEmailsList] = useState<LaraEmail[]>([]);

  // Search and Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'PRODUCE' | 'NONLTL'>('all');
  const [dateSearch, setDateSearch] = useState(''); // YYYY-MM-DD; matches pickup or outbound day
  const [statusFilter, setStatusFilter] = useState<'all' | 'PENDING SIGNATURE' | 'SIGNED' | 'BILLED'>('all');
  const [salespersonFilter, setSalespersonFilter] = useState<string>('all');
  const [viewTypeScopeFilter, setViewTypeScopeFilter] = useState<string>('all');

  // Column Customization states
  const [nonFloralColumns, setNonFloralColumns] = useState<ColumnSetting[]>(() => {
    try {
      const stored = localStorage.getItem(`ael_nonfloral_cols_${currentUserEmail}`);
      if (stored) return JSON.parse(stored);
    } catch {}
    return DEFAULT_NONFLORAL_COLUMNS;
  });
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);

  // Filter out financial columns for roles without financial view privileges (e.g. warehouse)
  const effectiveColumns = useMemo(() => {
    if (!canViewFinancials) {
      return nonFloralColumns.filter(c => c.id !== 'ratePerPallet');
    }
    return nonFloralColumns;
  }, [nonFloralColumns, canViewFinancials]);

  const effectiveFieldOptions = useMemo(() => {
    if (!canViewFinancials) {
      return NONFLORAL_FIELD_OPTIONS.filter(o => o.id !== 'ratePerPallet');
    }
    return NONFLORAL_FIELD_OPTIONS;
  }, [canViewFinancials]);

  // Interactive Column Width Resizing state
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    try {
      const stored = localStorage.getItem(`ael_nonfloral_widths_${currentUserEmail}`);
      if (stored) return { ...DEFAULT_COLUMN_WIDTHS, ...JSON.parse(stored) };
    } catch {}
    return DEFAULT_COLUMN_WIDTHS;
  });

  const handleResizeMouseDown = (colId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = columnWidths[colId] || DEFAULT_COLUMN_WIDTHS[colId] || 120;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const newWidth = Math.max(60, startWidth + deltaX);
      setColumnWidths(prev => {
        const next = { ...prev, [colId]: newWidth };
        try {
          localStorage.setItem(`ael_nonfloral_widths_${currentUserEmail}`, JSON.stringify(next));
        } catch {}
        return next;
      });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Advanced Criteria Builder states
  const [filterRules, setFilterRules] = useState<FilterRule[]>([]);
  const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] = useState(false);

  // Multi-Level Sorting & Header Sorting states
  const [sortRules, setSortRules] = useState<SortRule[]>([]);
  const [headerSort, setHeaderSort] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);
  const [isMultiSortOpen, setIsMultiSortOpen] = useState(false);

  // Saved / Smart Views states
  const [smartViews, setSmartViews] = useState<SmartView[]>(() => {
    try {
      const stored = localStorage.getItem(`ael_nonfloral_views_${currentUserEmail}`);
      if (stored) return JSON.parse(stored);
    } catch {}
    return [
      {
        id: 'view-pending-sig',
        name: 'Pending Signatures',
        rules: [{ id: '1', field: 'status', operator: 'equals', value: 'PENDING SIGNATURE' }],
        sortRules: [{ field: 'pickUpDate', direction: 'asc' }],
        createdAt: new Date().toISOString()
      },
      {
        id: 'view-active-produce',
        name: 'Active Produce',
        rules: [{ id: '2', field: 'produceNonltl', operator: 'equals', value: 'PRODUCE' }],
        sortRules: [{ field: 'pickUpDate', direction: 'asc' }],
        createdAt: new Date().toISOString()
      }
    ];
  });
  const [activeViewId, setActiveViewId] = useState<string | null>(null);

  // Inline Notes editing cache
  const [inlineNotes, setInlineNotes] = useState<{ [quoteNumber: string]: string }>({});

  // Modal form states
  const [isShipmentModalOpen, setIsShipmentModalOpen] = useState(false);
  const [editingShipment, setEditingLeadShipment] = useState<NonltlShipment | null>(null);

  const [isShipperModalOpen, setIsShipperModalOpen] = useState(false);
  const [editingShipper, setEditingShipper] = useState<Shipper | null>(null);

  const [isConsigneeModalOpen, setIsConsigneeModalOpen] = useState(false);
  const [editingConsignee, setEditingConsignee] = useState<Consignee | null>(null);

  const [isBillToModalOpen, setIsBillToModalOpen] = useState(false);
  const [editingBillTo, setEditingBillTo] = useState<BillTo | null>(null);

  const [newCommodityName, setNewCommodityName] = useState('');

  // Signature Draw modal states
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signShipment, setSignShipment] = useState<NonltlShipment | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Webhook integration feed state (Live simulation)
  const [webhookLogs, setWebhookLogs] = useState<{ id: string; time: string; event: string; payload: any }[]>([]);

  // Table pagination (LTL shipments + Lara queue)
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [laraPage, setLaraPage] = useState(1);
  useEffect(() => { setPage(1); setLaraPage(1); }, [activeSubTab, searchQuery, typeFilter, dateSearch, pageSize]);

  // Audit trail (written server-side on every save/delete)
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  useEffect(() => {
    if (activeSubTab !== 'audit') return;
    setAuditLoading(true);
    fetchAuditLog(200).then(setAuditEntries).finally(() => setAuditLoading(false));
  }, [activeSubTab]);

  // Initialize & Seed Database if empty
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [shipList, shList, consList, bList, commList, emailList] = await Promise.all([
          fetchNonltlShipments(),
          fetchShippers(),
          fetchConsignees(),
          fetchBillTos(),
          fetchCommodities(),
          fetchLaraEmails(),
        ]);

        // Default commodities if empty
        let finalComms = commList;
        if (commList.length === 0) {
          const defaultComms = ['Refrigerated Ferns', 'Eucalyptus Leaves', 'Leatherleaf', 'Soil & Palms', 'Moss Balls', 'Cut Foliage'];
          for (const c of defaultComms) {
            await saveCommodity({ commodity: c });
          }
          finalComms = await fetchCommodities();
        }

        // Default Shippers if empty
        let finalShippers = shList;
        if (shList.length === 0) {
          const defaultShippers: Shipper[] = [
            { shipperName: 'Alachua Fernery LLC', shipperAccount: 'SH1025', shipperEmail: 'shipping@alachuafern.com', shipperAddress: '102 Greenery Way', shipperCity: 'Alachua', shipperState: 'FL', shipperPhone: '386-555-0199' },
            { shipperName: 'Miami Foliage Growers', shipperAccount: 'SH2044', shipperEmail: 'orders@miamifoliage.com', shipperAddress: '5500 SW 87th Ave', shipperCity: 'Miami', shipperState: 'FL', shipperPhone: '305-555-0231' },
            { shipperName: 'Southern California Soil Co', shipperAccount: 'SH3002', shipperEmail: 'logistics@socalsoil.com', shipperAddress: '900 Desert Road', shipperCity: 'Oxnard', shipperState: 'CA', shipperPhone: '805-555-9011' }
          ];
          for (const s of defaultShippers) {
            await saveShipper(s);
          }
          finalShippers = await fetchShippers();
        }

        // Default Consignees if empty
        let finalConsignees = consList;
        if (consList.length === 0) {
          const defaultConsignees: Consignee[] = [
            { consigneeAccount: 'B010081', consigneeName: 'Boston Wholesale Floral', consigneeAddress: '400 Market Street', receivingWindow: '06:00 - 12:00', consigneeCity: 'Boston', consigneeState: 'MA', consigneeZip: '02108', consigneeEmail: 'bostonfloral@outlook.com', consigneePhone: '617-555-8812' },
            { consigneeAccount: 'B010155', consigneeName: 'Mid-Atlantic Greens', consigneeAddress: '88 Logistics Blvd', receivingWindow: '08:00 - 16:00', consigneeCity: 'Norfolk', consigneeState: 'VA', consigneeZip: '23501', consigneeEmail: 'receiving@midatlanticgreens.com', consigneePhone: '757-555-4421' },
            { consigneeAccount: 'B012000', consigneeName: 'Sewell Garden Center', consigneeAddress: '12 Village Green', receivingWindow: '07:30 - 13:30', consigneeCity: 'Sewell', consigneeState: 'NJ', consigneeZip: '08080', consigneeEmail: 'contact@sewellgardens.com', consigneePhone: '856-555-0122' }
          ];
          for (const c of defaultConsignees) {
            await saveConsignee(c);
          }
          finalConsignees = await fetchConsignees();
        }

        // Default Bill-Tos if empty
        let finalBillTos = bList;
        if (bList.length === 0) {
          const defaultBillTos: BillTo[] = [
            { billToAccount: 'BA-9901', billToName: 'National Florist Network', billToAddress: '1000 Network Parkway', billToCity: 'Chicago', billToState: 'IL', billToZip: '60601', billToEmail: 'billing@nationalflorist.net', signatureCell: '312-555-9022', signatureEmail: 'sign-alerts@nationalflorist.net' },
            { billToAccount: 'BA-2055', billToName: 'Global Green Importers', billToAddress: '44 Maritime Plaza', billToCity: 'Miami', billToState: 'FL', billToZip: '33131', billToEmail: 'ap@globalgreen.com', signatureCell: '786-555-1044', signatureEmail: 'verify@globalgreen.com' }
          ];
          for (const b of defaultBillTos) {
            await saveBillTo(b);
          }
          finalBillTos = await fetchBillTos();
        }

        // Default Shipments if empty
        let finalShipments = shipList;
        if (shipList.length === 0) {
          const defaultShipments: NonltlShipment[] = [
            {
              quoteNumber: 'NON102041',
              produceNonltl: 'NONLTL',
              status: 'PENDING SIGNATURE',
              pickUpDate: new Date().toISOString().split('T')[0],
              outboundDate: new Date(Date.now() + 86400000).toISOString().split('T')[0],
              shipperAccount: 'SH1025',
              shipperName: 'Alachua Fernery LLC',
              consignee: 'B010081',
              consigneeName: 'Boston Wholesale Floral, Boston, MA',
              consigneeAddress: '400 Market Street, Boston, MA, 02108',
              poNumber: 'PO-887102',
              palletCount: 14,
              commodity: 'Refrigerated Ferns',
              ratePerPallet: 125,
              billTo: 'BA-9901',
              billToName: 'National Florist Network',
              billToEmail: 'billing@nationalflorist.net',
              palletTenderedBy: "Lara's Trucking",
              apptReq: 'APPT REQ',
              receivingWindow: '06:00 - 12:00',
              signatureEmail: 'sign-alerts@nationalflorist.net',
              signatureCell: '312-555-9022',
              dateEntered: new Date().toISOString().split('T')[0]
            },
            {
              quoteNumber: 'NON102042',
              produceNonltl: 'PRODUCE',
              status: 'SIGNED',
              pickUpDate: new Date(Date.now() - 86400000).toISOString().split('T')[0],
              outboundDate: new Date().toISOString().split('T')[0],
              timeSigned: new Date(Date.now() - 3600000).toISOString(),
              shipperAccount: 'SH2044',
              shipperName: 'Miami Foliage Growers',
              consignee: 'B010155',
              consigneeName: 'Mid-Atlantic Greens, Norfolk, VA',
              consigneeAddress: '88 Logistics Blvd, Norfolk, VA, 23501',
              poNumber: 'PO-991204',
              palletCount: 8,
              commodity: 'Eucalyptus Leaves',
              ratePerPallet: 140,
              billTo: 'BA-2055',
              billToName: 'Global Green Importers',
              billToEmail: 'ap@globalgreen.com',
              palletTenderedBy: 'Shipper Delivery',
              apptReq: 'FCFS',
              receivingWindow: '08:00 - 16:00',
              signatureEmail: 'verify@globalgreen.com',
              signatureCell: '786-555-1044',
              dateEntered: new Date(Date.now() - 86400000).toISOString().split('T')[0]
            }
          ];
          for (const s of defaultShipments) {
            await saveNonltlShipment(s);
          }
          finalShipments = await fetchNonltlShipments();
        }

        setShipments(finalShipments);
        setShippersList(finalShippers);
        setConsigneesList(finalConsignees);
        setBillTosList(finalBillTos);
        setCommodityList(finalComms);
        setLaraEmailsList(emailList);
      } catch (err) {
        console.error("Error seeding Non Floral data:", err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Form Field helpers
  const handleShipperSelectInForm = (name: string, setForm: Function) => {
    const sh = shippersList.find(s => s.shipperName === name);
    if (sh) {
      setForm((prev: any) => ({
        ...prev,
        shipperName: sh.shipperName,
        shipperAccount: sh.shipperAccount || ''
      }));
    }
  };

  const handleConsigneeSelectInForm = (account: string, setForm: Function) => {
    const cons = consigneesList.find(c => c.consigneeAccount === account);
    if (cons) {
      setForm((prev: any) => ({
        ...prev,
        consignee: cons.consigneeAccount,
        consigneeName: `${cons.consigneeName}, ${cons.consigneeCity || ''}, ${cons.consigneeState || ''}`,
        consigneeAddress: `${cons.consigneeAddress || ''}, ${cons.consigneeCity || ''}, ${cons.consigneeState || ''}, ${cons.consigneeZip || ''}`,
        receivingWindow: cons.receivingWindow || ''
      }));
    }
  };

  const handleBillToSelectInForm = (account: string, setForm: Function) => {
    const bt = billTosList.find(b => b.billToAccount === account);
    if (bt) {
      setForm((prev: any) => ({
        ...prev,
        billTo: bt.billToAccount,
        billToName: bt.billToName,
        billToEmail: bt.billToEmail || '',
        signatureEmail: bt.signatureEmail || '',
        signatureCell: bt.signatureCell || ''
      }));
    }
  };

  // Triggering the AppSheet automated workflows / webhooks simulator
  const logWebhook = (event: string, payload: any) => {
    const logEntry = {
      id: `zap-${Date.now()}-${Math.random().toString(36).substr(2,4)}`,
      time: new Date().toLocaleTimeString(),
      event,
      payload
    };
    setWebhookLogs(prev => [logEntry, ...prev].slice(0, 15));
  };

  // Google Sheet sync: read the NONLTL tab with the user's Sheets token,
  // let the server upsert the rows, then reload the module data.
  const [sheetSyncing, setSheetSyncing] = useState(false);
  const [sheetSyncMsg, setSheetSyncMsg] = useState<string | null>(null);
  const handleSheetSync = async () => {
    if (sheetSyncing) return;
    if (!accessToken || isGoogleTokenExpired()) {
      notifyGoogleTokenExpired('Google access token expired before syncing NONLTL sheet.');
      setSheetSyncMsg('Google session expired. Please re-authenticate to sync the NONLTL sheet.');
      return;
    }
    setSheetSyncing(true);
    setSheetSyncMsg(null);
    try {
      const rows = await fetchSheetRows(accessToken, NONLTL_SHEET_ID, 'NONLTL');
      if (rows.length === 0) throw new Error('The NONLTL sheet returned no data rows.');
      const result = await sheetSyncNonltl(rows);
      const updated = await fetchNonltlShipments();
      setShipments(updated);
      setSheetSyncMsg(`Synced ${result.synced.toLocaleString()} shipments from the NONLTL sheet${result.skipped ? ` (${result.skipped} rows without a quote number skipped)` : ''}.`);
      logWebhook('Google Sheet Sync', { synced: result.synced, skipped: result.skipped });
    } catch (e: any) {
      setSheetSyncMsg(`Sheet sync failed: ${e.message}`);
    } finally {
      setSheetSyncing(false);
    }
  };

  // shipment CRUD actions
  const handleSaveShipmentAction = async (formData: any) => {
    setLoading(true);
    try {
      const isNew = !formData.quoteNumber;
      const quoteNum = formData.quoteNumber || `NON${Math.floor(100000 + Math.random() * 900000)}`;
      const payload: NonltlShipment = {
        ...formData,
        quoteNumber: quoteNum,
        palletCount: Number(formData.palletCount || 0),
        ratePerPallet: Number(formData.ratePerPallet || 0),
        dateEntered: formData.dateEntered || new Date().toISOString().slice(0, 16).replace('T', ' '),
      };

      await saveNonltlShipment(payload);
      const updated = await fetchNonltlShipments();
      setShipments(updated);

      // Webhook event dispatch
      logWebhook(isNew ? 'Shipment Created' : 'Shipment Updated', payload);

      setIsShipmentModalOpen(false);
      setEditingLeadShipment(null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteShipmentAction = async (quoteNumber: string) => {
    if (!window.confirm(`Are you sure you want to delete shipment ${quoteNumber}?`)) return;
    setLoading(true);
    try {
      await deleteNonltlShipment(quoteNumber);
      const updated = await fetchNonltlShipments();
      setShipments(updated);
      logWebhook('Shipment Deleted', { quoteNumber });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Status Transitions and Webhook Trigger logic
  const handleStatusAdvance = async (shipment: NonltlShipment) => {
    if (shipment.status === 'PENDING SIGNATURE') {
      // Open drawing pad signature
      setSignShipment(shipment);
      setSignatureModalOpen(true);
    } else if (shipment.status === 'SIGNED') {
      // Transition to BILLED
      setLoading(true);
      try {
        const payload: NonltlShipment = {
          ...shipment,
          status: 'BILLED',
          timeBilled: new Date().toISOString()
        };
        await saveNonltlShipment(payload);
        const updated = await fetchNonltlShipments();
        setShipments(updated);

        // Zapier AppSheet Webhook simulation trigger
        logWebhook('Zapier Webhook Fired - Shipment Billed', {
          webhookUrl: 'https://hooks.zapier.com/hooks/catch/149584/nonfloral',
          payload
        });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
  };

  // Capturing the signature and triggering workflow
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';

    const rect = canvas.getBoundingClientRect();
    const x = ('touches' in e) ? e.touches[0].clientX - rect.left : e.clientX - rect.left;
    const y = ('touches' in e) ? e.touches[0].clientY - rect.top : e.clientY - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = ('touches' in e) ? e.touches[0].clientX - rect.left : e.clientX - rect.left;
    const y = ('touches' in e) ? e.touches[0].clientY - rect.top : e.clientY - rect.top;

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleSaveSignature = async () => {
    if (!signShipment) return;
    setLoading(true);
    setSignatureModalOpen(false);

    try {
      const payload: NonltlShipment = {
        ...signShipment,
        status: 'SIGNED',
        timeSigned: new Date().toISOString()
      };

      await saveNonltlShipment(payload);
      const updated = await fetchNonltlShipments();
      setShipments(updated);

      // Trigger automatic generation of the "Lara Email Queue" record
      if (payload.palletTenderedBy === "Lara's Trucking") {
        const newLaraEmail: LaraEmail = {
          id: `LAR${Math.floor(100000 + Math.random() * 900000)}`,
          pickUpDate: payload.pickUpDate,
          palletTenderedBy: payload.palletTenderedBy,
          shipperName: payload.shipperName,
          consigneeName: payload.consigneeName,
          palletCount: payload.palletCount,
          commodity: payload.commodity,
          emailSent: null
        };
        await saveLaraEmail(newLaraEmail);
        const emails = await fetchLaraEmails();
        setLaraEmailsList(emails);
        logWebhook('Automation Triggered - Send to Lara', newLaraEmail);
      }

      logWebhook('Shipment Signed Successfully', payload);
      setSignShipment(null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Dispatch Lara email queue action
  const handleSendLaraEmail = async (id: string) => {
    setLoading(true);
    try {
      const email = laraEmailsList.find(e => e.id === id);
      if (email) {
        const payload = {
          ...email,
          emailSent: new Date().toISOString()
        };
        await saveLaraEmail(payload);
        const emails = await fetchLaraEmails();
        setLaraEmailsList(emails);
        logWebhook('Email Sent to Lara Logistics', payload);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Maintenance CRUD Action handlers
  const handleSaveShipper = async (data: Shipper) => {
    try {
      await saveShipper(data);
      const updated = await fetchShippers();
      setShippersList(updated);
      setIsShipperModalOpen(false);
      setEditingShipper(null);
      logWebhook('Shipper Saved', data);
    } catch (e) { console.error(e); }
  };

  const handleDeleteShipper = async (name: string) => {
    if (!window.confirm(`Delete shipper ${name}?`)) return;
    try {
      await deleteShipper(name);
      const updated = await fetchShippers();
      setShippersList(updated);
    } catch (e) { console.error(e); }
  };

  const handleSaveConsignee = async (data: Consignee) => {
    try {
      await saveConsignee(data);
      const updated = await fetchConsignees();
      setConsigneesList(updated);
      setIsConsigneeModalOpen(false);
      setEditingConsignee(null);
      logWebhook('Consignee Saved', data);
    } catch (e) { console.error(e); }
  };

  const handleDeleteConsignee = async (account: string) => {
    if (!window.confirm(`Delete consignee account ${account}?`)) return;
    try {
      await deleteConsignee(account);
      const updated = await fetchConsignees();
      setConsigneesList(updated);
    } catch (e) { console.error(e); }
  };

  const handleSaveBillTo = async (data: BillTo) => {
    try {
      await saveBillTo(data);
      const updated = await fetchBillTos();
      setBillTosList(updated);
      setIsBillToModalOpen(false);
      setEditingBillTo(null);
      logWebhook('Bill-To Saved', data);
    } catch (e) { console.error(e); }
  };

  const handleDeleteBillTo = async (account: string) => {
    if (!window.confirm(`Delete Bill-To account ${account}?`)) return;
    try {
      await deleteBillTo(account);
      const updated = await fetchBillTos();
      setBillTosList(updated);
    } catch (e) { console.error(e); }
  };

  const handleAddCommodity = async () => {
    if (!newCommodityName.trim()) return;
    try {
      await saveCommodity({ commodity: newCommodityName.trim() });
      const updated = await fetchCommodities();
      setCommodityList(updated);
      setNewCommodityName('');
    } catch (e) { console.error(e); }
  };

  const handleDeleteCommodity = async (name: string) => {
    try {
      await deleteCommodity(name);
      const updated = await fetchCommodities();
      setCommodityList(updated);
    } catch (e) { console.error(e); }
  };

  // Helper actions and state management handlers
  const uniqueSalespersons = useMemo(() => {
    const set = new Set<string>();
    shipments.forEach(s => {
      if (s.salesperson) set.add(s.salesperson);
    });
    (users || []).forEach(u => {
      if (u.role === 'sales' || u.role === 'editor' || u.role === 'admin') {
        const name = u.displayName || u.email.split('@')[0];
        if (name) set.add(name);
      }
    });
    return Array.from(set).filter(Boolean).sort();
  }, [shipments, users]);

  const handleSaveColumnPreferences = (cols: ColumnSetting[]) => {
    setNonFloralColumns(cols);
    try {
      localStorage.setItem(`ael_nonfloral_cols_${currentUserEmail}`, JSON.stringify(cols));
    } catch (e) {}
  };

  const handleSaveSmartView = (name: string) => {
    const newView: SmartView = {
      id: `view-${Date.now()}`,
      name,
      filterRules: filterRules,
      sortRules: sortRules,
      createdAt: new Date().toISOString()
    };
    const updated = [...smartViews, newView];
    setSmartViews(updated);
    setActiveViewId(newView.id);
    try {
      localStorage.setItem(`ael_nonfloral_views_${currentUserEmail}`, JSON.stringify(updated));
    } catch (e) {}
  };

  const handleSelectSmartView = (view: SmartView | null) => {
    if (!view) {
      setActiveViewId(null);
      setFilterRules([]);
      return;
    }
    setActiveViewId(view.id);
    setFilterRules(view.filterRules || []);
    if (view.sortRules && view.sortRules.length > 0) {
      setSortRules(view.sortRules);
    }
  };

  const handleDeleteSmartView = (id: string) => {
    const updated = smartViews.filter(v => v.id !== id);
    setSmartViews(updated);
    if (activeViewId === id) setActiveViewId(null);
    try {
      localStorage.setItem(`ael_nonfloral_views_${currentUserEmail}`, JSON.stringify(updated));
    } catch (e) {}
  };

  const handleHeaderClickSort = (fieldId: string) => {
    if (headerSort && headerSort.field === fieldId) {
      const nextDirection = headerSort.direction === 'asc' ? 'desc' : 'asc';
      setHeaderSort({ field: fieldId, direction: nextDirection });
      setSortRules([{ field: fieldId, direction: nextDirection }]);
    } else {
      setHeaderSort({ field: fieldId, direction: 'asc' });
      setSortRules([{ field: fieldId, direction: 'asc' }]);
    }
  };

  const handleInlineNotesSave = async (quoteNumber: string, noteText: string) => {
    const target = shipments.find(s => s.quoteNumber === quoteNumber);
    if (!target) return;
    const updated = { ...target, notes: noteText };
    await saveNonltlShipment(updated);
    setShipments(prev => prev.map(s => s.quoteNumber === quoteNumber ? updated : s));
  };

  const handleArriveDateSave = async (quoteNumber: string, dateStr: string) => {
    const target = shipments.find(s => s.quoteNumber === quoteNumber);
    if (!target) return;
    const updated = { ...target, arriveAtCustomerDate: dateStr };
    await saveNonltlShipment(updated);
    setShipments(prev => prev.map(s => s.quoteNumber === quoteNumber ? updated : s));
  };

  // Rule evaluation helper for advanced criteria builder
  const evaluateFilterRule = (item: NonltlShipment, rule: FilterRule): boolean => {
    if (!rule.field) return true;
    const rawVal = (item as any)[rule.field];
    const strVal = rawVal !== undefined && rawVal !== null ? String(rawVal).toLowerCase().trim() : '';
    const ruleVal = rule.value !== undefined && rule.value !== null ? String(rule.value).toLowerCase().trim() : '';

    switch (rule.operator) {
      case 'equals':
        return strVal === ruleVal;
      case 'not_equals':
        return strVal !== ruleVal;
      case 'contains':
        return strVal.includes(ruleVal);
      case 'does_not_contain':
        return !strVal.includes(ruleVal);
      case 'greater_than':
        return Number(rawVal || 0) > Number(rule.value || 0);
      case 'less_than':
        return Number(rawVal || 0) < Number(rule.value || 0);
      case 'is_empty':
        return strVal === '';
      case 'is_not_empty':
        return strVal !== '';
      case 'relative_date': {
        if (!rawVal) return false;
        const dateStr = String(rawVal).slice(0, 10);
        const todayStr = new Date().toLocaleDateString('en-CA');
        if (rule.value === 'Today') return dateStr === todayStr;
        if (rule.value === 'Yesterday') {
          const y = new Date(); y.setDate(y.getDate() - 1);
          return dateStr === y.toLocaleDateString('en-CA');
        }
        if (rule.value === 'This Week') {
          const now = new Date();
          const start = new Date(now); start.setDate(now.getDate() - now.getDay());
          const end = new Date(now); end.setDate(now.getDate() + (6 - now.getDay()));
          return dateStr >= start.toLocaleDateString('en-CA') && dateStr <= end.toLocaleDateString('en-CA');
        }
        if (rule.value === 'Last 7 Days') {
          const d = new Date(); d.setDate(d.getDate() - 7);
          return dateStr >= d.toLocaleDateString('en-CA') && dateStr <= todayStr;
        }
        if (rule.value === 'Last 30 Days') {
          const d = new Date(); d.setDate(d.getDate() - 30);
          return dateStr >= d.toLocaleDateString('en-CA') && dateStr <= todayStr;
        }
        if (rule.value === 'This Month') {
          return dateStr.startsWith(todayStr.slice(0, 7));
        }
        if (rule.value === 'Last Month') {
          const now = new Date();
          const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          return dateStr.startsWith(lm.toLocaleDateString('en-CA').slice(0, 7));
        }
        if (rule.value === 'This Quarter') {
          const now = new Date();
          const qMonth = Math.floor(now.getMonth() / 3) * 3;
          const qStart = new Date(now.getFullYear(), qMonth, 1).toLocaleDateString('en-CA');
          const qEnd = new Date(now.getFullYear(), qMonth + 3, 0).toLocaleDateString('en-CA');
          return dateStr >= qStart && dateStr <= qEnd;
        }
        if (rule.value === 'Next 30 Days') {
          const d = new Date(); d.setDate(d.getDate() + 30);
          return dateStr >= todayStr && dateStr <= d.toLocaleDateString('en-CA');
        }
        return true;
      }
      default:
        return true;
    }
  };

  // Filtered & Sorted shipments
  const filteredShipments = useMemo(() => {
    const todayStr = new Date().toLocaleDateString('en-CA');
    const pickupDay = (raw?: string | null): string | null => {
      if (!raw) return null;
      if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
      const d = new Date(raw);
      return isNaN(d.getTime()) ? null : d.toLocaleDateString('en-CA');
    };

    const list = shipments.filter(s => {
      // Search
      const searchStr = `${s.quoteNumber} ${s.shipperName} ${s.consigneeName} ${s.poNumber} ${s.commodity} ${s.pickUpDate || ''} ${s.outboundDate || ''} ${s.palletTenderedBy || ''} ${s.salesperson || ''} ${s.notes || ''}`.toLowerCase();
      if (searchQuery && !searchStr.includes(searchQuery.toLowerCase())) return false;

      // Module Type filter
      if (typeFilter !== 'all' && s.produceNonltl !== typeFilter) return false;

      // Salesperson Ownership filter
      if (salespersonFilter !== 'all' && s.salesperson !== salespersonFilter) return false;

      // Status filter
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;

      // View Type Scope filter
      if (viewTypeScopeFilter !== 'all') {
        if (viewTypeScopeFilter === 'Pending Signatures' && s.status !== 'PENDING SIGNATURE') return false;
        if (viewTypeScopeFilter === 'Active LTL' && s.status === 'BILLED') return false;
        if (viewTypeScopeFilter === 'Drafts' && s.status !== 'PENDING SIGNATURE') return false;
        if (viewTypeScopeFilter === 'Full Quote' && !s.quoteNumber) return false;
      }

      // Exact date search
      if (dateSearch) {
        const pu = pickupDay(s.pickUpDate);
        const ob = pickupDay(s.outboundDate);
        const arr = pickupDay(s.arriveAtCustomerDate);
        if (pu !== dateSearch && ob !== dateSearch && arr !== dateSearch) return false;
      }

      // Active vs History tab scoping
      const day = pickupDay(s.pickUpDate);
      if (activeSubTab === 'active' && day && day < todayStr) return false;
      if (activeSubTab === 'history' && (!day || day >= todayStr)) return false;

      // Advanced multi-rule criteria
      if (filterRules.length > 0) {
        const passesAllRules = filterRules.every(r => evaluateFilterRule(s, r));
        if (!passesAllRules) return false;
      }

      return true;
    });

    // Apply multi-level or single-column header sort, falling back to default Outbound Date sort
    const sorted = [...list].sort((a, b) => {
      if (sortRules && sortRules.length > 0) {
        for (const rule of sortRules) {
          const { field, direction } = rule;
          let valA = (a as any)[field];
          let valB = (b as any)[field];

          if (valA === undefined || valA === null) valA = '';
          if (valB === undefined || valB === null) valB = '';

          let cmp = 0;

          if (field === 'palletCount' || field === 'ratePerPallet') {
            const numA = Number(valA) || 0;
            const numB = Number(valB) || 0;
            cmp = numA - numB;
          } else if (field === 'outboundDate' || field === 'pickUpDate' || field === 'arriveAtCustomerDate') {
            const strA = String(valA).trim();
            const strB = String(valB).trim();
            if (!strA && !strB) cmp = 0;
            else if (!strA) cmp = 1;
            else if (!strB) cmp = -1;
            else {
              const timeA = new Date(strA).getTime();
              const timeB = new Date(strB).getTime();
              if (!isNaN(timeA) && !isNaN(timeB)) {
                cmp = timeA - timeB;
              } else {
                cmp = strA.localeCompare(strB);
              }
            }
          } else {
            cmp = String(valA).localeCompare(String(valB), undefined, { numeric: true, sensitivity: 'base' });
          }

          if (cmp !== 0) {
            return direction === 'desc' ? -cmp : cmp;
          }
        }
      }

      // Default sorting strictly by Outbound Date (or Pick-up Date)
      const da = a.outboundDate || a.pickUpDate || '';
      const db = b.outboundDate || b.pickUpDate || '';
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return activeSubTab === 'history' ? db.localeCompare(da) : da.localeCompare(db);
    });

    return sorted;
  }, [shipments, searchQuery, typeFilter, statusFilter, salespersonFilter, viewTypeScopeFilter, dateSearch, activeSubTab, filterRules, sortRules]);

  // Dynamic Aggregates for Sticky Summary Bar & KPI cards
  const activeShipments = filteredShipments;
  const totalPalletsNum = filteredShipments.reduce((sum, s) => sum + (s.palletCount || 0), 0);
  const totalAmountValue = filteredShipments.reduce((sum, s) => sum + ((s.palletCount || 0) * (s.ratePerPallet || 0)), 0);
  const pendingSignaturesCount = filteredShipments.filter(s => s.status === 'PENDING SIGNATURE').length;
  const totalBilledValue = filteredShipments
    .filter(s => s.status === 'BILLED')
    .reduce((sum, s) => sum + ((s.palletCount || 0) * (s.ratePerPallet || 0)), 0);

  const pagedShipments = filteredShipments.slice((page - 1) * pageSize, page * pageSize);

  // Lara queue shows only active work: pickups from today onward
  const todayLocal = new Date().toLocaleDateString('en-CA');
  const yesterdayLocal = (() => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toLocaleDateString('en-CA'); })();
  const laraDay = (raw?: string | null): string | null => {
    if (!raw) return null;
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d.toLocaleDateString('en-CA');
  };
  // Lara queue shows yesterday, today, and future pickups
  const activeLaraEmails = laraEmailsList.filter(e => {
    const day = laraDay(e.pickUpDate);
    return day !== null && day >= yesterdayLocal;
  });
  const pagedLaraEmails = activeLaraEmails.slice((laraPage - 1) * pageSize, laraPage * pageSize);

  // Shared pagination bar
  const PaginationBar = ({ total, current, onPage }: { total: number; current: number; onPage: (p: number) => void }) => {
    const pages = Math.max(1, Math.ceil(total / pageSize));
    if (total === 0) return null;
    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 dark:border-slate-800 text-xs">
        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
          <span>Rows per page</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="px-2 py-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded outline-none cursor-pointer"
          >
            {[25, 50, 100, 250].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span className="font-medium">
            {(current - 1) * pageSize + 1}–{Math.min(current * pageSize, total)} of {total.toLocaleString()}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onPage(1)} disabled={current === 1} className="px-2 py-1 rounded border border-slate-200 dark:border-slate-800 disabled:opacity-40 cursor-pointer font-bold">&laquo;</button>
          <button onClick={() => onPage(current - 1)} disabled={current === 1} className="px-2 py-1 rounded border border-slate-200 dark:border-slate-800 disabled:opacity-40 cursor-pointer font-bold">&lsaquo; Prev</button>
          <span className="px-2 font-bold text-slate-700 dark:text-slate-300">Page {current} / {pages}</span>
          <button onClick={() => onPage(current + 1)} disabled={current >= pages} className="px-2 py-1 rounded border border-slate-200 dark:border-slate-800 disabled:opacity-40 cursor-pointer font-bold">Next &rsaquo;</button>
          <button onClick={() => onPage(pages)} disabled={current >= pages} className="px-2 py-1 rounded border border-slate-200 dark:border-slate-800 disabled:opacity-40 cursor-pointer font-bold">&raquo;</button>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col min-w-0 bg-slate-50 dark:bg-slate-900 overflow-y-auto">
      
      {/* 1. Header with Stats & Live Sync Alert */}
      <div className="bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 shrink-0 transition-colors shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-1.5 bg-blue-100 dark:bg-blue-950 text-blue-600 dark:text-blue-400 rounded-lg">
              <Layers className="w-5 h-5 animate-pulse" />
            </span>
            <h1 className="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight">
              Produce &amp; Non Floral LTL Logistics
            </h1>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-xl">
            Armellini premium custom module. Built-in digital signature tracking, automatic shipper notification workflows, and real-time AppSheet database integrations.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Google Sheet sync */}
          <button
            onClick={handleSheetSync}
            disabled={sheetSyncing}
            title="Pull the NONLTL tab of the Google Sheet into the app"
            className="px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all duration-150 cursor-pointer bg-emerald-600 text-white shadow-md shadow-emerald-500/10 hover:bg-emerald-700 disabled:opacity-60 flex items-center gap-1.5"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${sheetSyncing ? 'animate-spin' : ''}`} />
            {sheetSyncing ? 'Syncing Sheet…' : 'Sync Google Sheet'}
          </button>
          <div className="w-px h-5 bg-slate-200 dark:bg-slate-800"></div>

          {/* Quick Sub-Tab togglers */}
          {[
            { id: 'active', label: 'Active LTL' },
            { id: 'history', label: 'Billing History' },
            { id: 'lara', label: 'Lara Email Queue' },
            { id: 'maintenance', label: 'Account Maintenance' },
            { id: 'audit', label: 'Audit Log' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTabState(tab.id as any)}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all duration-150 cursor-pointer ${
                activeSubTab === tab.id
                  ? 'bg-blue-600 text-white shadow-md shadow-blue-500/10'
                  : 'bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-850'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {sheetSyncMsg && (
        <div className={`mx-6 mt-4 p-3 rounded-lg text-xs font-semibold flex items-center justify-between gap-3 ${sheetSyncMsg.startsWith('Sheet sync failed') || sheetSyncMsg.startsWith('Connect') ? 'bg-red-50 dark:bg-red-950/25 text-red-700 dark:text-red-300' : 'bg-emerald-50 dark:bg-emerald-950/25 text-emerald-800 dark:text-emerald-300'}`}>
          <span>{sheetSyncMsg}</span>
          <button onClick={() => setSheetSyncMsg(null)} className="font-bold cursor-pointer opacity-60 hover:opacity-100">✕</button>
        </div>
      )}

      {/* 2. KPI Metrics Grid — hidden on Billing History */}
      {activeSubTab === 'active' && (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 p-6 shrink-0">
        <div className="bg-white dark:bg-slate-950 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-3.5 transition-colors">
          <div className="p-3 bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 rounded-lg">
            <Layers className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider font-bold">Active Shipments</p>
            <p className="text-xl font-black text-slate-800 dark:text-slate-100">{activeShipments.length}</p>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-950 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-3.5 transition-colors">
          <div className="p-3 bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 rounded-lg">
            <Signature className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider font-bold">Pending Signatures</p>
            <p className="text-xl font-black text-amber-600 dark:text-amber-400">{pendingSignaturesCount}</p>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-950 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-3.5 transition-colors">
          <div className="p-3 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 rounded-lg">
            <Package className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider font-bold">Active Pallet Pool</p>
            <p className="text-xl font-black text-indigo-600 dark:text-indigo-400">{totalPalletsNum}</p>
          </div>
        </div>

        {canViewFinancials ? (
          <div className="bg-white dark:bg-slate-950 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-3.5 transition-colors">
            <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 rounded-lg">
              <DollarSign className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider font-bold">Billed Pallets Revenue</p>
              <p className="text-xl font-black text-emerald-600 dark:text-emerald-400">${totalBilledValue.toLocaleString()}</p>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-950 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-3.5 transition-colors">
            <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 rounded-lg">
              <CheckCircle className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider font-bold">Billed Shipments</p>
              <p className="text-xl font-black text-emerald-600 dark:text-emerald-400">
                {filteredShipments.filter(s => s.status === 'BILLED').length} <span className="text-xs text-slate-400 font-normal">Completed</span>
              </p>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-slate-950 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-3.5 transition-colors">
          <div className="p-3 bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 rounded-lg">
            <Mail className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider font-bold">Lara Notification Queue</p>
            <p className="text-xl font-black text-purple-600 dark:text-purple-400">
              {activeLaraEmails.filter(e => !e.emailSent).length} <span className="text-xs text-slate-400 font-normal">Pending</span>
            </p>
          </div>
        </div>
      </div>
      )}

      {/* 3. Main Body */}
      <div className="flex-1 px-6 pb-12">
        {loading && (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-2.5 text-xs text-slate-500 font-semibold font-mono">Querying PostgreSQL Database...</span>
          </div>
        )}

        {!loading && (
          <div className="space-y-6">
            {/* SUBTAB: ACTIVE OR HISTORY SHIPMENTS */}
            {(activeSubTab === 'active' || activeSubTab === 'history') && (
              <div className="bg-white dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden transition-colors flex flex-col">
                
                {/* Top Smart View Selector */}
                <div className="px-4 pt-4">
                  <SmartViewSelector
                    smartViews={smartViews}
                    activeViewId={activeViewId}
                    onSelectView={handleSelectSmartView}
                    onSaveCurrentView={handleSaveSmartView}
                    onDeleteView={handleDeleteSmartView}
                    hasActiveFilters={filterRules.length > 0 || salespersonFilter !== 'all' || viewTypeScopeFilter !== 'all' || typeFilter !== 'all' || searchQuery !== ''}
                  />
                </div>

                {/* Table Quick Filters Toolbar */}
                <div className="p-4 border-b border-slate-100 dark:border-slate-900 bg-slate-50/50 dark:bg-slate-900/50 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2.5 flex-1">
                    {/* Search Input */}
                    <div className="relative min-w-[200px] flex-1 max-w-xs">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search Quote #, Shipper, Consignee, Notes..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-4 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs outline-none focus:border-blue-500"
                      />
                    </div>

                    {/* Salesperson Ownership Quick Filter */}
                    <select
                      value={salespersonFilter}
                      onChange={(e) => setSalespersonFilter(e.target.value)}
                      className="px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium outline-none"
                    >
                      <option value="all">All Salespersons</option>
                      {uniqueSalespersons.map((sp) => (
                        <option key={sp} value={sp}>{sp}</option>
                      ))}
                    </select>

                    {/* View Type Scope Quick Filter */}
                    <select
                      value={viewTypeScopeFilter}
                      onChange={(e) => setViewTypeScopeFilter(e.target.value)}
                      className="px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium outline-none"
                    >
                      <option value="all">All View Scopes</option>
                      <option value="Active LTL">Active LTL</option>
                      <option value="Pending Signatures">Pending Signatures</option>
                      <option value="Full Quote">Full Quote</option>
                      <option value="Drafts">Drafts</option>
                    </select>

                    {/* Module Type Filter */}
                    <select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value as any)}
                      className="px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium outline-none"
                    >
                      <option value="all">All Types</option>
                      <option value="NONLTL">NONLTL Only</option>
                      <option value="PRODUCE">PRODUCE Only</option>
                    </select>

                    {/* Status Filter */}
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as any)}
                      className="px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium outline-none"
                    >
                      <option value="all">All Statuses</option>
                      <option value="PENDING SIGNATURE">Pending Signature</option>
                      <option value="SIGNED">Signed</option>
                      <option value="BILLED">Billed</option>
                    </select>

                    {/* Exact date search */}
                    <div className="flex items-center gap-1">
                      <input
                        type="date"
                        value={dateSearch}
                        onChange={(e) => setDateSearch(e.target.value)}
                        title="Filter by Pickup / Outbound / Arrival Date"
                        className="px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs outline-none"
                      />
                      {dateSearch && (
                        <button
                          onClick={() => setDateSearch('')}
                          className="text-slate-400 hover:text-rose-600 text-xs font-bold px-1.5 py-1 rounded cursor-pointer"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Columns Customizer Trigger */}
                    <button
                      onClick={() => setIsColumnModalOpen(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-xs font-bold text-slate-700 dark:text-slate-300 transition cursor-pointer"
                      title="Show/Hide Columns"
                    >
                      <Columns className="w-3.5 h-3.5 text-blue-600" /> Show/Hide Columns
                    </button>

                    {/* Advanced Criteria Builder Trigger */}
                    <button
                      onClick={() => setIsAdvancedFilterOpen(true)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs font-bold transition cursor-pointer ${
                        filterRules.length > 0
                          ? 'bg-blue-50 dark:bg-blue-950 border-blue-300 text-blue-700 dark:text-blue-300'
                          : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:bg-slate-100 text-slate-700 dark:text-slate-300'
                      }`}
                    >
                      <Filter className="w-3.5 h-3.5 text-blue-600" /> Filter
                      {filterRules.length > 0 && (
                        <span className="bg-blue-600 text-white rounded-full text-[10px] w-4 h-4 flex items-center justify-center font-extrabold">
                          {filterRules.length}
                        </span>
                      )}
                    </button>

                    {/* Multi-Level Sort Trigger */}
                    <button
                      onClick={() => setIsMultiSortOpen(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-xs font-bold text-slate-700 dark:text-slate-300 transition cursor-pointer"
                    >
                      <ArrowUpDown className="w-3.5 h-3.5 text-blue-600" /> Sort
                    </button>

                    {/* Add Shipment Quote */}
                    <button
                      onClick={() => {
                        setEditingLeadShipment(null);
                        setIsShipmentModalOpen(true);
                      }}
                      className="flex items-center justify-center gap-1.5 px-3.5 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 transition cursor-pointer shadow-sm"
                    >
                      <Plus className="w-4 h-4" /> Add Quote
                    </button>
                  </div>
                </div>

                {/* Shipment List Table */}
                <div className="overflow-x-auto flex-1">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-slate-500 dark:text-slate-400 text-xs font-bold uppercase tracking-wider">
                        {effectiveColumns.filter(c => c.visible).map(col => {
                          const w = columnWidths[col.id] || DEFAULT_COLUMN_WIDTHS[col.id] || 120;
                          const activeSort = sortRules.find(r => r.field === col.id) || (headerSort?.field === col.id ? headerSort : null);
                          const isSortable = col.id !== 'actions';

                          return (
                            <th
                              key={col.id}
                              style={{ width: w, minWidth: w }}
                              onClick={() => isSortable && handleHeaderClickSort(col.id)}
                              className={`p-3 select-none relative group ${col.id === 'actions' ? 'text-right' : 'cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors'} ${activeSort ? 'text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-950/20' : ''}`}
                              title={isSortable ? `Click to sort by ${col.label}` : undefined}
                            >
                              <div className="flex items-center justify-between gap-1 pr-1">
                                <span className="truncate">{col.label}</span>
                                {isSortable && (
                                  <span className="shrink-0">
                                    {activeSort ? (
                                      activeSort.direction === 'asc' ? (
                                        <ArrowUp className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                                      ) : (
                                        <ArrowDown className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                                      )
                                    ) : (
                                      <ArrowUpDown className="w-3 h-3 text-slate-300 dark:text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    )}
                                  </span>
                                )}
                              </div>
                              {col.id !== 'actions' && (
                                <div
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    handleResizeMouseDown(col.id, e);
                                  }}
                                  className="absolute right-0 top-0 bottom-0 w-2.5 cursor-col-resize hover:bg-blue-500/40 active:bg-blue-600 transition-colors z-10"
                                  title="Drag to resize column"
                                />
                              )}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredShipments.length === 0 ? (
                        <tr>
                          <td colSpan={effectiveColumns.filter(c => c.visible).length || 10} className="p-8 text-center text-xs text-slate-400 font-medium">
                            No shipments found matching active filters and views.
                          </td>
                        </tr>
                      ) : (
                        pagedShipments.map((s) => (
                          <tr key={s.quoteNumber} className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-900/40 text-xs text-slate-800 dark:text-slate-200">
                            {effectiveColumns.filter(c => c.visible).map(col => {
                              switch (col.id) {
                                case 'produceNonltl':
                                  return (
                                    <td key={col.id} className="p-3 text-center">
                                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                        s.produceNonltl === 'PRODUCE' 
                                          ? 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 border border-emerald-100'
                                          : 'bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-400 border border-indigo-100'
                                      }`}>
                                        {s.produceNonltl}
                                      </span>
                                    </td>
                                  );
                                case 'quoteNumber':
                                  return (
                                    <td key={col.id} className="p-3 font-bold font-mono text-slate-900 dark:text-slate-100">
                                      {s.quoteNumber}
                                    </td>
                                  );
                                case 'shipperName':
                                  return (
                                    <td key={col.id} className="p-3">
                                      <div className="flex flex-col">
                                        <span className="font-bold text-slate-800 dark:text-slate-200">{s.shipperName}</span>
                                        <span className="text-[10px] text-slate-400 font-mono">Acc: {s.shipperAccount || '—'}</span>
                                      </div>
                                    </td>
                                  );
                                case 'outboundDate':
                                  return (
                                    <td key={col.id} className="p-3 font-mono text-xs text-indigo-600 dark:text-indigo-400 font-bold">
                                      {s.outboundDate || '—'}
                                    </td>
                                  );
                                case 'billToName':
                                  return (
                                    <td key={col.id} className="p-3">
                                      <div className="flex flex-col">
                                        <span className="font-bold text-slate-800 dark:text-slate-200">{s.billToName || s.billTo || '—'}</span>
                                        {s.billTo && s.billToName && <span className="text-[10px] text-slate-400 font-mono">Acc: {s.billTo}</span>}
                                      </div>
                                    </td>
                                  );
                                case 'consigneeName':
                                  return (
                                    <td key={col.id} className="p-3">
                                      <div className="flex flex-col">
                                        <span className="font-semibold">{s.consigneeName}</span>
                                        <span className="text-[10px] text-slate-400 font-mono font-medium">Acct: {s.consignee || '—'}</span>
                                      </div>
                                    </td>
                                  );
                                case 'pickupNumber':
                                  return (
                                    <td key={col.id} className="p-3 font-mono font-medium text-slate-900 dark:text-slate-100">
                                      {s.pickupNumber || '—'}
                                    </td>
                                  );
                                case 'apptReq':
                                  return (
                                    <td key={col.id} className="p-3">
                                      {s.apptReq === 'FCFS' ? (
                                        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 border border-emerald-200">
                                          FCFS
                                        </span>
                                      ) : (
                                        <span className="inline-block px-2 py-1 rounded text-[10px] font-bold bg-blue-100 dark:bg-blue-950/60 text-blue-800 dark:text-blue-300 border border-blue-200 whitespace-normal break-words leading-tight max-w-[260px]" title={s.receivingWindow}>
                                          {s.apptReq || 'APPT REQ'} {s.receivingWindow ? `(${s.receivingWindow})` : ''}
                                        </span>
                                      )}
                                    </td>
                                  );
                                case 'palletCount':
                                  return (
                                    <td key={col.id} className="p-3">
                                      <div className="flex flex-col">
                                        <span className="font-bold text-slate-700 dark:text-slate-300">{s.palletCount} Pallets</span>
                                        <span className="text-[10px] text-slate-400">{s.commodity || '—'}</span>
                                      </div>
                                    </td>
                                  );
                                case 'pickUpDate':
                                  return (
                                    <td key={col.id} className="p-3 font-mono text-xs text-blue-600 dark:text-blue-400 font-bold">
                                      {s.pickUpDate || '—'}
                                    </td>
                                  );
                                case 'arriveAtCustomerDate':
                                  return (
                                    <td key={col.id} className="p-3 font-mono text-xs">
                                      <input
                                        type="date"
                                        value={s.arriveAtCustomerDate || s.outboundDate || ''}
                                        onChange={(e) => handleArriveDateSave(s.quoteNumber, e.target.value)}
                                        className="px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-[11px] text-slate-800 dark:text-slate-200 focus:border-blue-500 outline-none cursor-pointer"
                                      />
                                    </td>
                                  );
                                case 'pickUpTime':
                                  return (
                                    <td key={col.id} className="p-3 font-mono text-xs font-semibold text-slate-700 dark:text-slate-300">
                                      {s.pickUpTime || '—'}
                                    </td>
                                  );
                                case 'poNumber':
                                  return (
                                    <td key={col.id} className="p-3 font-mono font-medium">{s.poNumber || '—'}</td>
                                  );
                                case 'palletTenderedBy':
                                  return (
                                    <td key={col.id} className="p-3">
                                      {s.palletTenderedBy ? (
                                        <span
                                          className="text-[9px] font-bold px-2 py-0.5 rounded text-white uppercase tracking-wide whitespace-nowrap"
                                          style={{ backgroundColor: CARRIER_COLORS[s.palletTenderedBy] || '#64748b' }}
                                        >
                                          {s.palletTenderedBy}
                                        </span>
                                      ) : '—'}
                                    </td>
                                  );
                                case 'ratePerPallet':
                                  return (
                                    <td key={col.id} className="p-3 font-mono font-bold text-slate-900 dark:text-slate-100">
                                      {s.ratePerPallet ? `$${s.ratePerPallet}/Plt` : '—'}
                                      <div className="text-[9px] text-slate-400 font-normal">Tot: ${((s.palletCount || 0) * (s.ratePerPallet || 0)).toLocaleString()}</div>
                                    </td>
                                  );
                                case 'salesperson':
                                  return (
                                    <td key={col.id} className="p-3 font-medium text-slate-700 dark:text-slate-300">
                                      {s.salesperson || 'Unassigned'}
                                    </td>
                                  );
                                case 'notes':
                                  return (
                                    <td key={col.id} className="p-3 min-w-[180px]">
                                      <input
                                        type="text"
                                        placeholder="Add notes..."
                                        value={inlineNotes[s.quoteNumber] !== undefined ? inlineNotes[s.quoteNumber] : (s.notes || '')}
                                        onChange={(e) => setInlineNotes({ ...inlineNotes, [s.quoteNumber]: e.target.value })}
                                        onBlur={(e) => handleInlineNotesSave(s.quoteNumber, e.target.value)}
                                        className="w-full px-2 py-1 text-xs border border-transparent hover:border-slate-300 dark:hover:border-slate-700 focus:border-blue-500 rounded bg-transparent focus:bg-white dark:focus:bg-slate-900 transition outline-none text-slate-800 dark:text-slate-200"
                                      />
                                    </td>
                                  );
                                case 'status':
                                  return (
                                    <td key={col.id} className="p-3">
                                      <div className="flex flex-col gap-1">
                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold text-center self-start ${
                                          s.status === 'PENDING SIGNATURE'
                                            ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-200'
                                            : s.status === 'SIGNED'
                                            ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 border border-blue-200'
                                            : 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200'
                                        }`}>
                                          {s.status}
                                        </span>
                                      </div>
                                    </td>
                                  );
                                case 'actions':
                                  return (
                                    <td key={col.id} className="p-3 text-right">
                                      <div className="flex justify-end items-center gap-1.5">
                                        {s.status !== 'BILLED' && (
                                          <button
                                            onClick={() => handleStatusAdvance(s)}
                                            className={`px-2 py-1 rounded text-[10px] font-bold flex items-center gap-1 transition-all cursor-pointer ${
                                              s.status === 'PENDING SIGNATURE'
                                                ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-sm'
                                                : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                                            }`}
                                            title={s.status === 'PENDING SIGNATURE' ? 'Capture Digital Signature' : 'Mark as Billed'}
                                          >
                                            {s.status === 'PENDING SIGNATURE' ? <Signature className="w-3 h-3" /> : <FileCheck className="w-3 h-3" />}
                                            {s.status === 'PENDING SIGNATURE' ? 'Sign' : 'Bill'}
                                          </button>
                                        )}
                                        <button
                                          onClick={() => { setEditingLeadShipment(s); setIsShipmentModalOpen(true); }}
                                          className="p-1 hover:bg-blue-50 dark:hover:bg-blue-950/40 text-blue-600 rounded transition cursor-pointer"
                                          title="Edit Shipment"
                                        >
                                          <Edit3 className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                          onClick={async () => {
                                            if (window.confirm(`Delete shipment quote ${s.quoteNumber}?`)) {
                                              await deleteNonltlShipment(s.quoteNumber);
                                              const list = await fetchNonltlShipments();
                                              setShipments(list);
                                            }
                                          }}
                                          className="p-1 hover:bg-red-50 text-red-500 rounded transition cursor-pointer"
                                          title="Delete Shipment"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    </td>
                                  );
                                default:
                                  return <td key={col.id} className="p-3">—</td>;
                              }
                            })}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* 5. Dynamic Aggregation Summary Bar (Sticky Footer) */}
                <div className="bg-slate-900 text-white p-3.5 border-t border-slate-800 flex flex-wrap items-center justify-between gap-4 text-xs font-mono font-bold sticky bottom-0 z-10 shadow-lg">
                  <div className="flex items-center gap-3">
                    <span className="bg-blue-600 text-white px-2.5 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider">
                      Dynamic Summary Totals
                    </span>
                    <span className="text-slate-300 font-sans">
                      Showing <strong className="text-white font-mono">{filteredShipments.length}</strong> matching shipments
                    </span>
                  </div>
                  <div className="flex items-center gap-6 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400 font-sans font-medium">Total Pallets:</span>
                      <span className="text-indigo-400 font-extrabold text-sm">{totalPalletsNum.toLocaleString()} Plts</span>
                    </div>
                    <div className="w-px h-4 bg-slate-700"></div>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400 font-sans font-medium">Total Amount ($):</span>
                      <span className="text-emerald-400 font-extrabold text-sm">${totalAmountValue.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Pagination bar */}
                <div className="p-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
                  <PaginationBar total={filteredShipments.length} current={page} onPage={setPage} />
                </div>

              </div>
            )}

            {/* SUBTAB: LARA NOTIFICATION EMAIL QUEUE */}
            {activeSubTab === 'lara' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Lara Email Entries List */}
                <div className="lg:col-span-2 bg-white dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden p-6 transition-colors">
                  <div className="mb-4">
                    <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100 uppercase tracking-wider flex items-center gap-1.5">
                      <Mail className="w-4 h-4 text-purple-500" /> Lara Logistics Carrier Emails
                    </h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      Workflows scheduled automatically when non-floral shipments of "Lara's Trucking" are signed.
                    </p>
                  </div>

                  <div className="space-y-4">
                    {activeLaraEmails.length === 0 ? (
                      <p className="text-center py-8 text-xs text-slate-400 font-medium font-mono">No active notifications — only pickups from yesterday onward appear here.</p>
                    ) : (
                      pagedLaraEmails.map(e => (
                        <div key={e.id} className="border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30 p-4 rounded-xl flex justify-between items-start text-xs">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <span className="font-black text-slate-800 dark:text-slate-100 font-mono text-sm">{e.id}</span>
                              <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${
                                e.emailSent 
                                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-400' 
                                  : 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-400'
                              }`}>
                                {e.emailSent ? 'SENT' : 'PENDING NOTIFICATION'}
                              </span>
                            </div>

                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 font-medium text-slate-600 dark:text-slate-400">
                              <div><span className="font-bold text-slate-800 dark:text-slate-200">Shipper:</span> {e.shipperName}</div>
                              <div><span className="font-bold text-slate-800 dark:text-slate-200">Consignee:</span> {e.consigneeName}</div>
                              <div><span className="font-bold text-slate-800 dark:text-slate-200">Tendered By:</span> {e.palletTenderedBy}</div>
                              <div><span className="font-bold text-slate-800 dark:text-slate-200">Pallets Count:</span> {e.palletCount} ({e.commodity})</div>
                            </div>
                            
                            {e.emailSent && (
                              <div className="text-[10px] text-slate-400 font-mono">Sent on: {new Date(e.emailSent).toLocaleString()}</div>
                            )}
                          </div>

                          <div className="flex gap-2">
                            {!e.emailSent && (
                              <button
                                onClick={() => handleSendLaraEmail(e.id)}
                                className="px-3 py-1 bg-purple-600 text-white hover:bg-purple-700 font-bold rounded flex items-center gap-1 transition"
                              >
                                <Send className="w-3 h-3" /> Dispatch Email
                              </button>
                            )}
                            <button
                              onClick={async () => {
                                if (window.confirm("Delete this email log?")) {
                                  await deleteLaraEmail(e.id);
                                  const list = await fetchLaraEmails();
                                  setLaraEmailsList(list);
                                }
                              }}
                              className="p-1 bg-red-50 hover:bg-red-100 text-red-500 rounded transition"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <PaginationBar total={activeLaraEmails.length} current={laraPage} onPage={setLaraPage} />
                </div>

                {/* Automation logs & Webhook simulator */}
                <div className="bg-white dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-6 flex flex-col transition-colors">
                  <div className="mb-4">
                    <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100 uppercase tracking-wider flex items-center gap-1.5">
                      <Zap className="w-4 h-4 text-amber-500" /> AppSheet Webhook Integration
                    </h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      Live simulated Zapier Webhook responses on shipment billing, status updates, and digital signatures.
                    </p>
                  </div>

                  <div className="flex-1 bg-slate-950 rounded-xl p-4 font-mono text-[10px] text-emerald-400 overflow-y-auto max-h-[350px] border border-slate-800">
                    <p className="text-slate-500 mb-2">// Webhook system initialized. Awaiting AppSheet triggers...</p>
                    {webhookLogs.length === 0 ? (
                      <p className="text-slate-600 italic">// No webhook events dispatched yet.</p>
                    ) : (
                      webhookLogs.map(log => (
                        <div key={log.id} className="border-b border-slate-900 pb-2 mb-2 space-y-1">
                          <div className="flex justify-between text-slate-400 font-bold">
                            <span>Event: {log.event}</span>
                            <span>{log.time}</span>
                          </div>
                          <pre className="text-emerald-300 overflow-x-auto whitespace-pre-wrap">
                            {JSON.stringify(log.payload, null, 2)}
                          </pre>
                        </div>
                      ))
                    )}
                  </div>
                </div>

              </div>
            )}

            {/* SUBTAB: MAINTENANCE TAB */}
            {/* SUBTAB: AUDIT LOG */}
            {activeSubTab === 'audit' && (
              <div className="bg-white dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Audit Log</h3>
                    <p className="text-xs text-slate-400">Every add, change, and delete in this module — recorded automatically with the signed-in user.</p>
                  </div>
                  <button
                    onClick={() => { setAuditLoading(true); fetchAuditLog(200).then(setAuditEntries).finally(() => setAuditLoading(false)); }}
                    className="text-xs bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-300 px-3 py-1.5 rounded font-bold hover:bg-slate-200 dark:hover:bg-slate-800 transition cursor-pointer"
                  >
                    Refresh
                  </button>
                </div>
                <div className="overflow-x-auto">
                  {auditLoading ? (
                    <p className="text-xs text-slate-400 text-center py-10">Loading audit trail…</p>
                  ) : auditEntries.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-10">No audit entries yet — they appear as soon as anyone adds or changes a record.</p>
                  ) : (
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="border-b border-slate-200 dark:border-slate-800 text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                          <th className="p-3">When</th>
                          <th className="p-3">User</th>
                          <th className="p-3">Action</th>
                          <th className="p-3">Record</th>
                          <th className="p-3">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditEntries.map(a => (
                          <tr key={a.id} className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-900/40 text-slate-700 dark:text-slate-300">
                            <td className="p-3 font-mono text-[10px] whitespace-nowrap">{new Date(a.timestamp).toLocaleString()}</td>
                            <td className="p-3 font-semibold">{a.userEmail || '—'}</td>
                            <td className="p-3">
                              <span className={`text-[9px] font-bold px-2 py-0.5 rounded uppercase ${a.action === 'DELETED' ? 'bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400' : 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400'}`}>
                                {a.action}
                              </span>
                            </td>
                            <td className="p-3 font-mono text-[10px]">{a.entity}{a.entityId ? ` · ${a.entityId}` : ''}</td>
                            <td className="p-3 text-slate-500 dark:text-slate-400 max-w-[280px] truncate" title={a.details || ''}>{a.details || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {activeSubTab === 'maintenance' && (
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                
                {/* Lateral maintenance nav */}
                <div className="space-y-1 shrink-0">
                  <div className="text-slate-400 font-mono text-[9px] font-bold tracking-wider uppercase mb-2 px-1">Maintenance Scope</div>
                  {[
                    { id: 'shippers', label: 'Shippers Directory', icon: Users },
                    { id: 'consignees', label: 'Consignees List', icon: Briefcase },
                    { id: 'billtos', label: 'Bill-To Accounts', icon: FileText },
                    { id: 'commodities', label: 'LTL Commodities', icon: Layers }
                  ].map((sub) => {
                    const Icon = sub.icon;
                    return (
                      <button
                        key={sub.id}
                        onClick={() => setMaintenanceSubTab(sub.id as any)}
                        className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-xs font-bold transition-all text-left cursor-pointer ${
                          maintenanceSubTab === sub.id
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-850'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {sub.label}
                      </button>
                    );
                  })}
                </div>

                {/* Maintenance workspace grid */}
                <div className="lg:col-span-3 bg-white dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-6 overflow-hidden transition-colors">
                  
                  {/* shippers panel */}
                  {maintenanceSubTab === 'shippers' && (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100 uppercase tracking-wider">Shippers Accounts</h2>
                          <p className="text-xs text-slate-400">Manage origin shipping facilities and contact email setups.</p>
                        </div>
                        <button
                          onClick={() => {
                            setEditingShipper(null);
                            setIsShipperModalOpen(true);
                          }}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded text-xs flex items-center gap-1 transition"
                        >
                          <PlusCircle className="w-3.5 h-3.5" /> Add Shipper
                        </button>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                          <thead>
                            <tr className="border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 text-xs font-bold uppercase tracking-wider">
                              <th className="p-3">Shipper Name</th>
                              <th className="p-3">Account</th>
                              <th className="p-3">Email</th>
                              <th className="p-3">Address</th>
                              <th className="p-3">Phone</th>
                              <th className="p-3 text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {shippersList.map((s) => (
                              <tr key={s.shipperName} className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-900/40 text-xs">
                                <td className="p-3 font-bold text-slate-800 dark:text-slate-100">{s.shipperName}</td>
                                <td className="p-3 font-mono">{s.shipperAccount || '—'}</td>
                                <td className="p-3 font-mono">{s.shipperEmail || '—'}</td>
                                <td className="p-3">{s.shipperAddress}, {s.shipperCity}, {s.shipperState}</td>
                                <td className="p-3 font-mono">{s.shipperPhone || '—'}</td>
                                <td className="p-3 text-right">
                                  <div className="flex justify-end gap-1.5">
                                    <button
                                      onClick={() => {
                                        setEditingShipper(s);
                                        setIsShipperModalOpen(true);
                                      }}
                                      className="p-1 hover:bg-blue-50 dark:hover:bg-blue-950/40 text-blue-600 rounded transition"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteShipper(s.shipperName)}
                                      className="p-1 hover:bg-red-50 text-red-500 rounded transition"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* consignees panel */}
                  {maintenanceSubTab === 'consignees' && (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100 uppercase tracking-wider">Consignees Directory</h2>
                          <p className="text-xs text-slate-400">Manage drop-off facilities, receiving windows, and billing links.</p>
                        </div>
                        <button
                          onClick={() => {
                            setEditingConsignee(null);
                            setIsConsigneeModalOpen(true);
                          }}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded text-xs flex items-center gap-1 transition"
                        >
                          <PlusCircle className="w-3.5 h-3.5" /> Add Consignee
                        </button>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                          <thead>
                            <tr className="border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 text-xs font-bold uppercase tracking-wider">
                              <th className="p-3">Account</th>
                              <th className="p-3">Facility Name</th>
                              <th className="p-3">Hours / Receiving Window</th>
                              <th className="p-3">Full Address</th>
                              <th className="p-3">Email & Phone</th>
                              <th className="p-3 text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {consigneesList.map((c) => (
                              <tr key={c.consigneeAccount} className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-900/40 text-xs">
                                <td className="p-3 font-mono font-bold text-slate-800 dark:text-slate-100">{c.consigneeAccount}</td>
                                <td className="p-3 font-bold">{c.consigneeName}</td>
                                <td className="p-3 font-mono text-purple-600 dark:text-purple-400 font-semibold">{c.receivingWindow || '—'}</td>
                                <td className="p-3">{c.consigneeAddress}, {c.consigneeCity}, {c.consigneeState} {c.consigneeZip}</td>
                                <td className="p-3">
                                  <div className="flex flex-col text-[10px]">
                                    <span>{c.consigneeEmail || '—'}</span>
                                    <span>{c.consigneePhone || '—'}</span>
                                  </div>
                                </td>
                                <td className="p-3 text-right">
                                  <div className="flex justify-end gap-1.5">
                                    <button
                                      onClick={() => {
                                        setEditingConsignee(c);
                                        setIsConsigneeModalOpen(true);
                                      }}
                                      className="p-1 hover:bg-blue-50 dark:hover:bg-blue-950/40 text-blue-600 rounded transition"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteConsignee(c.consigneeAccount)}
                                      className="p-1 hover:bg-red-50 text-red-500 rounded transition"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* bill-tos panel */}
                  {maintenanceSubTab === 'billtos' && (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100 uppercase tracking-wider">Bill-To Partners</h2>
                          <p className="text-xs text-slate-400">Manage billing addresses, signature contact details, and automated invoices.</p>
                        </div>
                        <button
                          onClick={() => {
                            setEditingBillTo(null);
                            setIsBillToModalOpen(true);
                          }}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded text-xs flex items-center gap-1 transition"
                        >
                          <PlusCircle className="w-3.5 h-3.5" /> Add Bill-To Account
                        </button>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                          <thead>
                            <tr className="border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 text-xs font-bold uppercase tracking-wider">
                              <th className="p-3">Account</th>
                              <th className="p-3">Partner Name</th>
                              <th className="p-3">Billing Email</th>
                              <th className="p-3">Signature Alerts Email</th>
                              <th className="p-3">Alert Cell</th>
                              <th className="p-3 text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {billTosList.map((b) => (
                              <tr key={b.billToAccount} className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-900/40 text-xs">
                                <td className="p-3 font-mono font-bold text-slate-800 dark:text-slate-100">{b.billToAccount}</td>
                                <td className="p-3 font-bold">{b.billToName}</td>
                                <td className="p-3 font-mono">{b.billToEmail || '—'}</td>
                                <td className="p-3 font-mono">{b.signatureEmail || '—'}</td>
                                <td className="p-3 font-mono">{b.signatureCell || '—'}</td>
                                <td className="p-3 text-right">
                                  <div className="flex justify-end gap-1.5">
                                    <button
                                      onClick={() => {
                                        setEditingBillTo(b);
                                        setIsBillToModalOpen(true);
                                      }}
                                      className="p-1 hover:bg-blue-50 dark:hover:bg-blue-950/40 text-blue-600 rounded transition"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteBillTo(b.billToAccount)}
                                      className="p-1 hover:bg-red-50 text-red-500 rounded transition"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* commodities panel */}
                  {maintenanceSubTab === 'commodities' && (
                    <div className="space-y-4">
                      <div>
                        <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100 uppercase tracking-wider">Logistics Commodities list</h2>
                        <p className="text-xs text-slate-400">Custom flower, produce, and foliage freight commodity settings.</p>
                      </div>

                      <div className="flex gap-2 max-w-md">
                        <input
                          type="text"
                          placeholder="Enter new commodity (e.g. Oak Logs, Cut Hydrangeas)"
                          value={newCommodityName}
                          onChange={(e) => setNewCommodityName(e.target.value)}
                          className="flex-1 px-3 py-1.5 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded text-xs outline-none focus:bg-white"
                        />
                        <button
                          onClick={handleAddCommodity}
                          className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded text-xs flex items-center gap-1 transition"
                        >
                          <PlusCircle className="w-3.5 h-3.5" /> Add
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                        {commoditiesList.map(c => (
                          <div key={c.commodity} className="p-3 border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30 rounded-xl flex items-center justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
                            <span className="flex items-center gap-1.5"><Layers className="w-3.5 h-3.5 text-blue-500" /> {c.commodity}</span>
                            <button
                              onClick={() => handleDeleteCommodity(c.commodity)}
                              className="text-red-500 hover:text-red-700 p-1 rounded"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                </div>

              </div>
            )}

          </div>
        )}
      </div>

      {/* --- POPUP MODAL: ADD / EDIT SHIPMENT --- */}
      {isShipmentModalOpen && (
        <ShipmentFormModal
          isOpen={isShipmentModalOpen}
          onClose={() => setIsShipmentModalOpen(false)}
          onSave={handleSaveShipmentAction}
          editingShipment={editingShipment}
          shippersList={shippersList}
          consigneesList={consigneesList}
          billTosList={billTosList}
          commoditiesList={commoditiesList}
          handleShipperSelectInForm={handleShipperSelectInForm}
          handleConsigneeSelectInForm={handleConsigneeSelectInForm}
          handleBillToSelectInForm={handleBillToSelectInForm}
          existingQuoteNumbers={shipments.map(s => s.quoteNumber)}
          canViewFinancials={canViewFinancials}
        />
      )}

      {/* --- POPUP MODAL: ADD / EDIT SHIPPER --- */}
      {isShipperModalOpen && (
        <ShipperFormModal
          isOpen={isShipperModalOpen}
          onClose={() => setIsShipperModalOpen(false)}
          onSave={handleSaveShipper}
          editingShipper={editingShipper}
        />
      )}

      {/* --- POPUP MODAL: ADD / EDIT CONSIGNEE --- */}
      {isConsigneeModalOpen && (
        <ConsigneeFormModal
          isOpen={isConsigneeModalOpen}
          onClose={() => setIsConsigneeModalOpen(false)}
          onSave={handleSaveConsignee}
          editingConsignee={editingConsignee}
          existingConsignees={consigneesList}
        />
      )}

      {/* --- POPUP MODAL: ADD / EDIT BILL-TO --- */}
      {isBillToModalOpen && (
        <BillToFormModal
          isOpen={isBillToModalOpen}
          onClose={() => setIsBillToModalOpen(false)}
          onSave={handleSaveBillTo}
          editingBillTo={editingBillTo}
        />
      )}

      {/* --- DIGITAL SIGNATURE CANVAS MODAL --- */}
      <AnimatePresence>
        {signatureModalOpen && signShipment && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setSignatureModalOpen(false)} />
            
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-slate-950 w-full max-w-lg rounded-xl border border-slate-200 dark:border-slate-800 shadow-2xl relative z-10 overflow-hidden"
            >
              <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Signature className="w-5 h-5 text-amber-500" />
                  <div>
                    <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Capture Digital Signature</h3>
                    <p className="text-[10px] text-slate-400">Quote: {signShipment.quoteNumber} | PO: {signShipment.poNumber}</p>
                  </div>
                </div>
                <button onClick={() => setSignatureModalOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-slate-50 dark:bg-slate-900 p-3 rounded-lg text-xs space-y-1.5 text-slate-600 dark:text-slate-400 font-medium">
                  <div><span className="font-bold text-slate-800 dark:text-slate-200">Carrier / Consignee:</span> {signShipment.consigneeName}</div>
                  <div><span className="font-bold text-slate-800 dark:text-slate-200">Address:</span> {signShipment.consigneeAddress}</div>
                  <div><span className="font-bold text-slate-800 dark:text-slate-200">Pallets:</span> {signShipment.palletCount} Plts of {signShipment.commodity}</div>
                </div>

                <div className="border border-slate-300 dark:border-slate-700 bg-white rounded-lg overflow-hidden relative shadow-inner">
                  <canvas
                    ref={canvasRef}
                    width={440}
                    height={160}
                    className="w-full h-40 cursor-crosshair block"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                  <div className="absolute top-2 left-3 bg-slate-100/80 px-2 py-0.5 rounded text-[9px] text-slate-500 font-semibold font-mono pointer-events-none">SIGN HERE</div>
                </div>

                <div className="flex justify-between items-center text-xs">
                  <button onClick={clearCanvas} className="px-3 py-1.5 bg-slate-100 dark:bg-slate-900 hover:bg-slate-200 text-slate-600 dark:text-slate-300 font-bold rounded">
                    Clear Pad
                  </button>

                  <div className="flex gap-2">
                    <button onClick={() => setSignatureModalOpen(false)} className="px-3 py-1.5 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-900 rounded font-bold">
                      Cancel
                    </button>
                    <button onClick={handleSaveSignature} className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded shadow">
                      Accept Signature
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      {/* --- COLUMN CUSTOMIZER MODAL --- */}
      {isColumnModalOpen && (
        <ColumnCustomizerModal
          isOpen={isColumnModalOpen}
          onClose={() => setIsColumnModalOpen(false)}
          userEmail={currentUserEmail}
          initialColumns={effectiveColumns}
          onSavePreferences={handleSaveColumnPreferences}
        />
      )}

      {/* --- ADVANCED CRITERIA BUILDER MODAL --- */}
      {isAdvancedFilterOpen && (
        <AdvancedFilterModal
          isOpen={isAdvancedFilterOpen}
          onClose={() => setIsAdvancedFilterOpen(false)}
          rules={filterRules}
          onApplyRules={(rules) => setFilterRules(rules)}
          fieldOptions={effectiveFieldOptions}
        />
      )}

      {/* --- MULTI-LEVEL SORT MODAL --- */}
      {isMultiSortOpen && (
        <MultiSortModal
          isOpen={isMultiSortOpen}
          onClose={() => setIsMultiSortOpen(false)}
          sortRules={sortRules}
          onApplySort={(rules) => setSortRules(rules)}
          fieldOptions={effectiveFieldOptions}
        />
      )}

    </div>
  );
}

// ======================== MODAL FORMS SUB-COMPONENTS ========================

function ShipmentFormModal({
  isOpen, onClose, onSave, editingShipment,
  shippersList, consigneesList, billTosList, commoditiesList,
  handleShipperSelectInForm, handleConsigneeSelectInForm, handleBillToSelectInForm,
  existingQuoteNumbers, canViewFinancials = true, uniqueSalespersons = []
}: any) {
  const [form, setForm] = useState({
    quoteNumber: '',
    produceNonltl: 'NONLTL',
    status: 'PENDING SIGNATURE',
    pickUpDate: new Date().toISOString().split('T')[0],
    outboundDate: new Date(Date.now() + 86400000).toISOString().split('T')[0],
    shipperAccount: '',
    shipperName: '',
    consignee: '',
    consigneeName: '',
    consigneeAddress: '',
    poNumber: '',
    palletCount: 1,
    commodity: '',
    ratePerPallet: 100,
    billTo: '',
    billToName: '',
    billToEmail: '',
    palletTenderedBy: "Lara's Trucking",
    apptReq: 'APPT REQ',
    receivingWindow: '',
    signatureEmail: '',
    signatureCell: '',
    dateEntered: '',
    tenderedCarrier: ''
  });

  useEffect(() => {
    if (editingShipment) {
      setForm({ ...editingShipment });
    } else {
      // Quote numbers are system-generated and unique — never hand-typed
      const taken = new Set((existingQuoteNumbers || []) as string[]);
      let quote = '';
      do { quote = `NON${Math.floor(1000000 + Math.random() * 9000000)}`; } while (taken.has(quote));
      setForm(prev => ({ ...prev, quoteNumber: quote }));

      // Pre-fill first items as defaults
      if (shippersList.length > 0) handleShipperSelectInForm(shippersList[0].shipperName, setForm);
      if (consigneesList.length > 0) handleConsigneeSelectInForm(consigneesList[0].consigneeAccount, setForm);
      if (billTosList.length > 0) handleBillToSelectInForm(billTosList[0].billToAccount, setForm);
      if (commoditiesList.length > 0) setForm(prev => ({ ...prev, commodity: commoditiesList[0].commodity }));
    }
  }, [editingShipment, shippersList, consigneesList, billTosList, commoditiesList]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />

      <div className="bg-white dark:bg-slate-950 w-full max-w-2xl rounded-xl border border-slate-200 dark:border-slate-800 shadow-2xl relative z-10 overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center shrink-0">
          <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 uppercase tracking-wider flex items-center gap-1.5">
            <Layers className="w-4 h-4 text-blue-500" /> {editingShipment ? 'Edit LTL Shipment' : 'Create Shipment Quotation'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 overflow-y-auto space-y-4 text-xs font-semibold">

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-500 dark:text-slate-400 mb-1">Quote Number (auto-generated)</label>
              <input
                type="text"
                readOnly
                value={form.quoteNumber ?? ''}
                className="w-full px-3 py-1.5 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none font-mono text-blue-600 dark:text-blue-400 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-slate-500 dark:text-slate-400 mb-1">Date Entered</label>
              <input
                type="text"
                readOnly
                value={form.dateEntered || 'Set automatically on save'}
                className="w-full px-3 py-1.5 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none text-slate-500 dark:text-slate-400 cursor-not-allowed"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-500 dark:text-slate-400 mb-1">Module Type</label>
              <select
                value={form.produceNonltl ?? ''}
                onChange={(e) => setForm({ ...form, produceNonltl: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
              >
                <option value="NONLTL">NON-FLORAL LTL</option>
                <option value="PRODUCE">PRODUCE LTL</option>
              </select>
            </div>
            
            <div>
              <label className="block text-slate-500 dark:text-slate-400 mb-1">Logistics Status</label>
              <select
                value={form.status ?? ''}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
              >
                <option value="PENDING SIGNATURE">PENDING SIGNATURE</option>
                <option value="SIGNED">SIGNED</option>
                <option value="BILLED">BILLED</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-slate-500 dark:text-slate-400 mb-1">Pick Up Date</label>
              <input
                type="date"
                value={form.pickUpDate ?? ''}
                onChange={(e) => setForm({ ...form, pickUpDate: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
              />
            </div>
            <div>
              <label className="block text-slate-500 dark:text-slate-400 mb-1">Arrive at Customer Date</label>
              <input
                type="date"
                value={(form as any).arriveAtCustomerDate || form.outboundDate || ''}
                onChange={(e) => setForm({ ...form, arriveAtCustomerDate: e.target.value, outboundDate: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none font-semibold text-blue-600 dark:text-blue-400"
              />
            </div>
            <div>
              <label className="block text-slate-500 dark:text-slate-400 mb-1">Pick-Up Time</label>
              <input
                type="text"
                placeholder="e.g. 08:30 AM"
                value={(form as any).pickUpTime ?? ''}
                onChange={(e) => setForm({ ...form, pickUpTime: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-500 dark:text-slate-400 mb-1">Salesperson / Owner</label>
              <input
                type="text"
                list="nonfloral-salespersons"
                placeholder="e.g. Danean Allan"
                value={(form as any).salesperson ?? ''}
                onChange={(e) => setForm({ ...form, salesperson: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none font-medium"
              />
              <datalist id="nonfloral-salespersons">
                {uniqueSalespersons.map((sp) => (
                  <option key={sp} value={sp} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-slate-500 dark:text-slate-400 mb-1">Operational Notes</label>
              <input
                type="text"
                placeholder="Comments / Receiving instructions..."
                value={(form as any).notes ?? ''}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
              />
            </div>
          </div>

          <div className="border-t border-slate-100 dark:border-slate-800 pt-4 space-y-4">
            <h4 className="text-blue-500 font-bold uppercase tracking-widest text-[10px]">1. Origin &amp; Shipper</h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <label className="block text-slate-500 dark:text-slate-400 mb-1">Shipper Facility</label>
                <select
                  value={form.shipperName ?? ''}
                  onChange={(e) => handleShipperSelectInForm(e.target.value, setForm)}
                  className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
                >
                  {shippersList.map((s: any) => (
                    <option key={s.shipperName} value={s.shipperName}>{s.shipperName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-slate-500 dark:text-slate-400 mb-1">Shipper Account #</label>
                <input
                  type="text"
                  readOnly
                  value={form.shipperAccount ?? ''}
                  className="w-full px-3 py-1.5 bg-slate-100 dark:bg-slate-900 text-slate-500 border border-transparent dark:border-slate-800 rounded-md outline-none cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-slate-100 dark:border-slate-800 pt-4 space-y-4">
            <h4 className="text-blue-500 font-bold uppercase tracking-widest text-[10px]">2. Destination &amp; Consignee Facility</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-slate-500 dark:text-slate-400 mb-1">Consignee Account</label>
                <select
                  value={form.consignee ?? ''}
                  onChange={(e) => handleConsigneeSelectInForm(e.target.value, setForm)}
                  className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
                >
                  {consigneesList.map((c: any) => (
                    <option key={c.consigneeAccount} value={c.consigneeAccount}>{c.consigneeAccount}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-slate-500 dark:text-slate-400 mb-1">Full Facility Details</label>
                <input
                  type="text"
                  readOnly
                  value={form.consigneeName ?? ''}
                  className="w-full px-3 py-1.5 bg-slate-100 dark:bg-slate-900 text-slate-500 border border-transparent dark:border-slate-800 rounded-md outline-none cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-slate-100 dark:border-slate-800 pt-4 space-y-4">
            <h4 className="text-blue-500 font-bold uppercase tracking-widest text-[10px]">3. Bill To Billing</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-slate-500 dark:text-slate-400 mb-1">Billing Account</label>
                <select
                  value={form.billTo ?? ''}
                  onChange={(e) => handleBillToSelectInForm(e.target.value, setForm)}
                  className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
                >
                  {billTosList.map((b: any) => (
                    <option key={b.billToAccount} value={b.billToAccount}>{b.billToAccount}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-slate-500 dark:text-slate-400 mb-1">Billing Entity</label>
                <input
                  type="text"
                  readOnly
                  value={form.billToName ?? ''}
                  className="w-full px-3 py-1.5 bg-slate-100 dark:bg-slate-900 text-slate-500 border border-transparent dark:border-slate-800 rounded-md outline-none cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-slate-100 dark:border-slate-800 pt-4 space-y-4">
            <h4 className="text-blue-500 font-bold uppercase tracking-widest text-[10px]">4. Carrier Operations & Shipment Metrics</h4>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-slate-500 dark:text-slate-400 mb-1">Pallet Tendered By</label>
                <select
                  value={form.palletTenderedBy ?? ''}
                  onChange={(e) => setForm({ ...form, palletTenderedBy: e.target.value })}
                  className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
                >
                  <option value="Lara's Trucking">Lara's Trucking</option>
                  <option value="Shipper Delivery">Shipper Delivery</option>
                  <option value="AEL PLM Pickup">AEL PLM Pickup</option>
                  <option value="AEL MIA Pickup">AEL MIA Pickup</option>
                  <option value="AEL OXN Pickup">AEL OXN Pickup</option>
                  <option value="AEL PIE Pickup">AEL PIE Pickup</option>
                </select>
              </div>
              <div>
                <label className="block text-slate-500 dark:text-slate-400 mb-1">Appointment Type</label>
                <select
                  value={form.apptReq ?? ''}
                  onChange={(e) => setForm({ ...form, apptReq: e.target.value as any })}
                  className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
                >
                  <option value="APPT REQ">APPT REQ</option>
                  <option value="FCFS">FCFS</option>
                </select>
              </div>
              <div>
                <label className="block text-slate-500 dark:text-slate-400 mb-1">Purchase Order #</label>
                <input
                  type="text"
                  placeholder="PO-88123"
                  value={form.poNumber ?? ''}
                  onChange={(e) => setForm({ ...form, poNumber: e.target.value })}
                  className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
                />
              </div>
              <div>
                <label className="block text-slate-500 dark:text-slate-400 mb-1">Pick Up #</label>
                <input
                  type="text"
                  placeholder="PU-99412"
                  value={(form as any).pickupNumber ?? ''}
                  onChange={(e) => setForm({ ...form, pickupNumber: e.target.value })}
                  className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
                />
              </div>
            </div>

            <div className={`grid ${canViewFinancials ? 'grid-cols-3' : 'grid-cols-2'} gap-4`}>
              <div>
                <label className="block text-slate-500 dark:text-slate-400 mb-1">Pallet Count</label>
                <input
                  type="number"
                  value={form.palletCount ?? ''}
                  onChange={(e) => setForm({ ...form, palletCount: Number(e.target.value) })}
                  className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
                />
              </div>
              <div>
                <label className="block text-slate-500 dark:text-slate-400 mb-1">Commodity Type</label>
                <select
                  value={form.commodity ?? ''}
                  onChange={(e) => setForm({ ...form, commodity: e.target.value })}
                  className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
                >
                  {commoditiesList.map((c: any) => (
                    <option key={c.commodity} value={c.commodity}>{c.commodity}</option>
                  ))}
                </select>
              </div>
              {canViewFinancials && (
                <div>
                  <label className="block text-slate-500 dark:text-slate-400 mb-1">Rate Per Pallet ($)</label>
                  <input
                    type="number"
                    step="any"
                    value={form.ratePerPallet ?? ''}
                    onChange={(e) => setForm({ ...form, ratePerPallet: Number(e.target.value) })}
                    className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
                  />
                </div>
              )}
            </div>
          </div>

        </div>

        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex justify-between shrink-0 text-xs">
          <button onClick={onClose} className="px-3 py-1.5 text-slate-500 hover:bg-slate-100 rounded font-bold">
            Cancel
          </button>
          <button onClick={() => onSave(form)} className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded shadow-md">
            Save Shipment
          </button>
        </div>
      </div>
    </div>
  );
}

function ShipperFormModal({ isOpen, onClose, onSave, editingShipper }: any) {
  const [form, setForm] = useState<Shipper>({
    shipperName: '',
    shipperAccount: '',
    shipperEmail: '',
    shipperAddress: '',
    shipperCity: '',
    shipperState: '',
    shipperPhone: ''
  });

  useEffect(() => {
    if (editingShipper) setForm(editingShipper);
  }, [editingShipper]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      
      <div className="bg-white dark:bg-slate-950 w-full max-w-md rounded-xl border border-slate-200 dark:border-slate-800 shadow-2xl relative z-10 overflow-hidden flex flex-col text-xs font-semibold">
        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center shrink-0">
          <h3 className="font-bold text-slate-800 dark:text-slate-100 uppercase tracking-wider">
            {editingShipper ? 'Edit Shipper Account' : 'Add Shipper'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-slate-500 mb-1">Shipper Facility Name</label>
            <input
              type="text"
              readOnly={!!editingShipper}
              placeholder="e.g. Alachua Growers"
              value={form.shipperName ?? ''}
              onChange={(e) => setForm({ ...form, shipperName: e.target.value })}
              className={`w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none ${editingShipper ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : ''}`}
            />
          </div>
          <div>
            <label className="block text-slate-500 mb-1">Account Number</label>
            <input
              type="text"
              placeholder="e.g. SH-1234"
              value={form.shipperAccount ?? ''}
              onChange={(e) => setForm({ ...form, shipperAccount: e.target.value })}
              className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
            />
          </div>
          <div>
            <label className="block text-slate-500 mb-1">Notification Email</label>
            <input
              type="email"
              placeholder="shipping@example.com"
              value={form.shipperEmail ?? ''}
              onChange={(e) => setForm({ ...form, shipperEmail: e.target.value })}
              className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
            />
          </div>
          <div>
            <label className="block text-slate-500 mb-1">Phone Number</label>
            <input
              type="text"
              placeholder="386-555-0100"
              value={form.shipperPhone ?? ''}
              onChange={(e) => setForm({ ...form, shipperPhone: e.target.value })}
              className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className="block text-slate-500 mb-1">Street Address</label>
              <input
                type="text"
                placeholder="100 Main St"
                value={form.shipperAddress ?? ''}
                onChange={(e) => setForm({ ...form, shipperAddress: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
              />
            </div>
            <div>
              <label className="block text-slate-500 mb-1">City</label>
              <input
                type="text"
                placeholder="Alachua"
                value={form.shipperCity ?? ''}
                onChange={(e) => setForm({ ...form, shipperCity: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex justify-between text-xs shrink-0">
          <button onClick={onClose} className="px-3 py-1.5 text-slate-500 hover:bg-slate-100 rounded font-bold">Cancel</button>
          <button onClick={() => onSave(form)} className="px-4 py-1.5 bg-blue-600 text-white rounded font-bold">Save Shipper</button>
        </div>
      </div>
    </div>
  );
}

function ConsigneeFormModal({ isOpen, onClose, onSave, editingConsignee, existingConsignees = [] }: any) {
  const [form, setForm] = useState<Consignee>({
    consigneeAccount: '',
    consigneeName: '',
    consigneeAddress: '',
    receivingWindow: '',
    consigneeAddress2: '',
    consigneeCity: '',
    consigneeState: '',
    consigneeZip: '',
    consigneeEmail: '',
    consigneePhone: '',
    consigneeFax: ''
  });

  // Type-ahead over the imported consignee directory: typing a name or
  // account shows matches; picking one fills the whole form (account number
  // and facility name included) from the directory record.
  const [searchField, setSearchField] = useState<'name' | 'account' | null>(null);

  const query = searchField === 'account' ? form.consigneeAccount : form.consigneeName;
  const matches = (!editingConsignee && searchField && query && query.trim().length >= 2)
    ? (existingConsignees as Consignee[]).filter(c =>
        c.consigneeName?.toLowerCase().includes(query.trim().toLowerCase()) ||
        c.consigneeAccount?.toLowerCase().includes(query.trim().toLowerCase())
      ).slice(0, 8)
    : [];

  const pickConsignee = (c: Consignee) => {
    setForm({ ...c });
    setSearchField(null);
  };

  const suggestionList = matches.length > 0 && (
    <div className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl z-20 max-h-52 overflow-y-auto">
      {matches.map((c: Consignee) => (
        <button
          key={c.consigneeAccount}
          type="button"
          onMouseDown={(e) => { e.preventDefault(); pickConsignee(c); }}
          className="w-full text-left px-3 py-2 hover:bg-blue-50 dark:hover:bg-blue-950/40 border-b border-slate-100 dark:border-slate-800 last:border-b-0 cursor-pointer"
        >
          <span className="font-bold text-slate-800 dark:text-slate-200">{c.consigneeName}</span>
          <span className="ml-2 font-mono text-[10px] text-blue-600 dark:text-blue-400">{c.consigneeAccount}</span>
          {c.consigneeCity && <span className="ml-2 text-[10px] text-slate-400">{c.consigneeCity}{c.consigneeState ? `, ${c.consigneeState}` : ''}</span>}
        </button>
      ))}
    </div>
  );

  useEffect(() => {
    if (editingConsignee) setForm(editingConsignee);
  }, [editingConsignee]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      
      <div className="bg-white dark:bg-slate-950 w-full max-w-md rounded-xl border border-slate-200 dark:border-slate-800 shadow-2xl relative z-10 overflow-hidden flex flex-col text-xs font-semibold">
        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center shrink-0">
          <h3 className="font-bold text-slate-800 dark:text-slate-100 uppercase tracking-wider">
            {editingConsignee ? 'Edit Consignee Facility' : 'Add Consignee'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {!editingConsignee && (
            <p className="text-[11px] text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30 rounded-lg px-3 py-2 font-semibold">
              Search the consignee directory by name or account — picking a match fills the account number and facility name automatically.
            </p>
          )}
          <div className="relative">
            <label className="block text-slate-500 mb-1">Consignee Account Number</label>
            <input
              type="text"
              readOnly={!!editingConsignee}
              placeholder="e.g. B010081 — type to search"
              value={form.consigneeAccount ?? ''}
              onChange={(e) => { setForm({ ...form, consigneeAccount: e.target.value }); setSearchField('account'); }}
              onBlur={() => setTimeout(() => setSearchField(f => f === 'account' ? null : f), 150)}
              className={`w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none ${editingConsignee ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : ''}`}
            />
            {searchField === 'account' && suggestionList}
          </div>
          <div className="relative">
            <label className="block text-slate-500 mb-1">Facility Name</label>
            <input
              type="text"
              placeholder="e.g. Boston Wholesale Floral — type to search"
              value={form.consigneeName ?? ''}
              onChange={(e) => { setForm({ ...form, consigneeName: e.target.value }); if (!editingConsignee) setSearchField('name'); }}
              onBlur={() => setTimeout(() => setSearchField(f => f === 'name' ? null : f), 150)}
              className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
            />
            {searchField === 'name' && suggestionList}
          </div>
          <div>
            <label className="block text-slate-500 mb-1">Receiving Hours Window</label>
            <input
              type="text"
              placeholder="e.g. 06:00 - 12:00"
              value={form.receivingWindow ?? ''}
              onChange={(e) => setForm({ ...form, receivingWindow: e.target.value })}
              className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-slate-500 mb-1">Email</label>
              <input
                type="email"
                placeholder="receiv@example.com"
                value={form.consigneeEmail ?? ''}
                onChange={(e) => setForm({ ...form, consigneeEmail: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
              />
            </div>
            <div>
              <label className="block text-slate-500 mb-1">Phone</label>
              <input
                type="text"
                placeholder="617-555-8812"
                value={form.consigneePhone ?? ''}
                onChange={(e) => setForm({ ...form, consigneePhone: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className="block text-slate-500 mb-1">Address</label>
              <input
                type="text"
                placeholder="400 Market St"
                value={form.consigneeAddress ?? ''}
                onChange={(e) => setForm({ ...form, consigneeAddress: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
              />
            </div>
            <div>
              <label className="block text-slate-500 mb-1">City</label>
              <input
                type="text"
                placeholder="Boston"
                value={form.consigneeCity ?? ''}
                onChange={(e) => setForm({ ...form, consigneeCity: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex justify-between text-xs shrink-0">
          <button onClick={onClose} className="px-3 py-1.5 text-slate-500 hover:bg-slate-100 rounded font-bold">Cancel</button>
          <button onClick={() => onSave(form)} className="px-4 py-1.5 bg-blue-600 text-white rounded font-bold">Save Consignee</button>
        </div>
      </div>
    </div>
  );
}

function BillToFormModal({ isOpen, onClose, onSave, editingBillTo }: any) {
  const [form, setForm] = useState<BillTo>({
    billToAccount: '',
    billToName: '',
    billToAddress: '',
    billToAddress2: '',
    billToCity: '',
    billToState: '',
    billToZip: '',
    billToEmail: '',
    signatureCell: '',
    signatureEmail: '',
    billToPhone: '',
    billToFax: ''
  });

  useEffect(() => {
    if (editingBillTo) setForm(editingBillTo);
  }, [editingBillTo]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      
      <div className="bg-white dark:bg-slate-950 w-full max-w-md rounded-xl border border-slate-200 dark:border-slate-800 shadow-2xl relative z-10 overflow-hidden flex flex-col text-xs font-semibold">
        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center shrink-0">
          <h3 className="font-bold text-slate-800 dark:text-slate-100 uppercase tracking-wider">
            {editingBillTo ? 'Edit Bill-To Partner' : 'Add Bill-To Partner'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block text-slate-500 mb-1">Billing Account Code</label>
            <input
              type="text"
              readOnly={!!editingBillTo}
              placeholder="e.g. BA-9901"
              value={form.billToAccount ?? ''}
              onChange={(e) => setForm({ ...form, billToAccount: e.target.value })}
              className={`w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none ${editingBillTo ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : ''}`}
            />
          </div>
          <div>
            <label className="block text-slate-500 mb-1">Partner Entity Name</label>
            <input
              type="text"
              placeholder="e.g. National Florist Network"
              value={form.billToName ?? ''}
              onChange={(e) => setForm({ ...form, billToName: e.target.value })}
              className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
            />
          </div>
          <div>
            <label className="block text-slate-500 mb-1">Billing Invoices Email</label>
            <input
              type="email"
              placeholder="billing@example.com"
              value={form.billToEmail ?? ''}
              onChange={(e) => setForm({ ...form, billToEmail: e.target.value })}
              className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-2 border-t border-slate-100 dark:border-slate-800 pt-3">
            <div>
              <label className="block text-slate-500 mb-1">Alerts Signature Cell</label>
              <input
                type="text"
                placeholder="312-555-9022"
                value={form.signatureCell ?? ''}
                onChange={(e) => setForm({ ...form, signatureCell: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
              />
            </div>
            <div>
              <label className="block text-slate-500 mb-1">Alerts Signature Email</label>
              <input
                type="email"
                placeholder="sign@example.com"
                value={form.signatureEmail ?? ''}
                onChange={(e) => setForm({ ...form, signatureEmail: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md outline-none"
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex justify-between text-xs shrink-0">
          <button onClick={onClose} className="px-3 py-1.5 text-slate-500 hover:bg-slate-100 rounded font-bold">Cancel</button>
          <button onClick={() => onSave(form)} className="px-4 py-1.5 bg-blue-600 text-white rounded font-bold">Save Partner</button>
        </div>
      </div>
    </div>
  );
}
